import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
	Autocomplete,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Icon,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Tooltip,
} from "@mui/material";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { formatMoneyPunto } from "utils/formatters";
import { diagnosticoErrorFacturacion } from "services/facturacionErrores";
import excelIcon from "assets/images/excel.png";
import VoucherClienteDialog from "./VoucherClienteDialog";
import VoucherFacturaImpresion from "./VoucherFacturaImpresion";
import { downloadVoucherFacturacionExcel } from "./voucherFacturacionExcel";
import { TRATAMIENTO_IVA_LABEL, formatPorcentaje, ivaLabel, previewImportes } from "./voucherIva";
import {
	ESTADOS_FACTURA_FILTRO,
	MOTIVO_DESCARTE_MAX,
	badgeFactura,
	buildCorrecciones,
	correccionesIniciales,
	describirBloqueo,
	esValidacionFiscalRechazada,
	esFalloTemporalPrevio,
	facturaBloqueanteDeError,
	fechaEmisionFactura,
	intervencionLabel,
	mensajeErrorFacturacion,
	textosRevisionIncidente,
	tieneIncidente,
	validarMotivoDescarte,
} from "./voucherFacturaAcciones";

const SEDES_QUERY = gql`
  query sedesVoucherFacturacion {
    sedes { id nombre cuit ptoVenta condicionIva direccion }
  }
`;

// Reglas del emisor de la sede: actividades con su IVA sugerido y opciones de IVA admitidas.
const CATALOGO_FISCAL_QUERY = gql`
  query voucherCatalogoFiscalFacturacion($idSede: ID!) {
    voucherCatalogoFiscal(idSede: $idSede) {
      cuitEmisor nombreEmisor
      opcionesIva { clave tratamientoIva alicuotaIvaId porcentaje descripcion }
      actividades { codigo descripcion opcionIvaSugerida }
      advertencias
    }
  }
`;

// El usuario elige libremente la letra; si no corresponde, ARCA rechaza el comprobante.
const TIPOS_FACTURA = ["A", "B", "C"];

const CLIENTES_QUERY = gql`
  query voucherClientesFacturacion($search: String, $page: Int, $limit: Int) {
    voucherClientes(search: $search, page: $page, limit: $limit) {
      rows {
        id tipoCliente nombre apellido razonSocial tipoDocumento numeroDocumento
        condicionIva cuit calle numero codigoPostal
      }
      pagination { total pages page limit }
    }
  }
`;

const RESUMEN_QUERY = gql`
  query resumenVoucherGrupoFacturacion($idGrupo: ID!, $idSede: ID!) {
    voucherGrupoFacturacionResumen(idGrupo: $idGrupo, idSede: $idSede) {
      idGrupo idSede tipoGrupo totalMontoAsignado totalMontoUtilizado porcentajeMontoUtilizado
      totalUnidadesAsignadas totalUnidadesUtilizadas porcentajeUnidadesUtilizadas totalAplicado
      totalFacturado pendienteFacturar saldoFacturacion estadoSaldo montoSaldo ultimaFactura
      condicionIvaEmisor
      bloqueoEmision { idFactura estadoFactura motivo mensaje }
      ultimaFacturaUsuario { id nombre apellido }
      clienteSugerido {
        id tipoCliente nombre apellido razonSocial tipoDocumento numeroDocumento condicionIva cuit
      }
    }
  }
`;

const FACTURACION_DETAIL_QUERY = gql`
  query detalleActualVoucherGrupoFacturacion($idGrupo: ID!, $idSede: ID!) {
    voucherGrupoFacturacionDetalle(idGrupo: $idGrupo, idSede: $idSede) {
      idGrupo idSede nombreGrupo nombreSede fechaCorte generadoEn
      resumen {
        totalAplicado totalFacturado pendienteFacturar saldoFacturacion estadoSaldo montoSaldo
      }
      pedidos {
        idPedido fechaPedido fechaCierrePedido estadoPedido totalPedido
        cliente { id nombre apellido dni }
        consumos {
          idVenta idVoucher tipoVoucher cantidadUtilizada montoDescuento totalVenta
        }
      }
      facturas {
        id fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId estado total conceptoTexto clienteSnapshot
        clienteFacturado cuitClienteFacturado numeroDeFacturaAfip CAE CAEFchVto error createdAt
        clienteFacturacion { id tipoCliente nombre apellido razonSocial cuit }
        usuarioFactura { id nombre apellido }
      }
    }
  }
`;

const FACTURAS_QUERY = gql`
  query facturasVoucherGrupo($idGrupo: ID!, $idSede: ID!, $search: String, $page: Int!, $limit: Int!, $order: VoucherGrupoFacturaOrden!, $estado: VoucherGrupoFacturaEstado, $idFactura: ID) {
    voucherGrupoFacturas(idGrupo: $idGrupo, idSede: $idSede, search: $search, page: $page, limit: $limit, order: $order, estado: $estado, idFactura: $idFactura) {
      rows {
        id fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId estado total conceptoTexto clienteSnapshot
        saldoAntesFactura montoSugeridoAlEmitir montoExceso saldoDespuesFactura fechaCorteSaldo
        comparacion {
          saldoArrastrado nuevosDescuentos sugerido facturado resultado
          estadoResultado exacta advertencia
        }
        pto_vta tipo_cbte tipo_doc nro_doc imp_iva21 subtotal21 importeNoAlcanzado importeExento concepto
        numeroDeFacturaAfip CAE CAEFchVto url error createdAt fundamentoNormativo
        estadoFiscal { estado requiereIntervencion intervencion codigo referencia idOperacion requestId proximoReintento diagnostico }
        idOperacionFiscalActual motivoDescarte fechaDescarte
        usuarioDescarte { id nombre apellido }
        acciones { verificar reintentar descartar resolverIncidente motivo }
        incidentes { id intervencion }
        sede {
          id nombre nombreFantasia cuit ptoVenta condicionIva direccion
          incioActividades ingresosBrutos email
        }
        clienteFacturacion {
          id tipoCliente nombre apellido razonSocial tipoDocumento numeroDocumento
          condicionIva cuit calle numero codigoPostal
        }
        usuarioFactura { id nombre apellido }
      }
      pagination { total pages page limit }
    }
  }
`;

// Retoma una factura pendiente: nunca crea otra factura.
const CONTINUAR_FACTURA_MUTATION = gql`
  mutation continuarFacturaVoucherDesdeTab($idFactura: ID!) {
    continuarFacturaVoucher(idFactura: $idFactura) {
      id estado error numeroDeFacturaAfip CAE CAEFchVto
      estadoFiscal { estado requiereIntervencion intervencion codigo referencia idOperacion requestId proximoReintento diagnostico }
    }
  }
`;

// Nueva decision de emision sobre la misma factura tras un fallo definitivo.
// Solo admite corregir letra, actividad e IVA; el receptor sale de la ficha del cliente.
const REINTENTAR_FACTURA_MUTATION = gql`
  mutation reintentarFacturaVoucherDesdeTab($input: ReintentarFacturaVoucherInput!) {
    reintentarFacturaVoucher(input: $input) {
      id estado error numeroDeFacturaAfip tipoFactura CAE CAEFchVto idOperacionFiscalActual
      estadoFiscal { estado requiereIntervencion intervencion codigo referencia idOperacion requestId proximoReintento diagnostico }
    }
  }
`;

// Descarta una factura con error: no anula comprobantes fiscales.
const DESCARTAR_FACTURA_MUTATION = gql`
  mutation descartarFacturaVoucherDesdeTab($input: DescartarFacturaVoucherInput!) {
    descartarFacturaVoucher(input: $input) {
      id estado motivoDescarte fechaDescarte
    }
  }
`;

// Marca como revisados los incidentes fiscales de una factura (no cambia datos fiscales).
const RESOLVER_INCIDENTE_MUTATION = gql`
  mutation resolverIncidenteFacturaVoucherDesdeTab($input: ResolverIncidenteFacturaVoucherInput!) {
    resolverIncidenteFacturaVoucher(input: $input) {
      id estado
    }
  }
`;

const FACTURAR_MUTATION = gql`
  mutation facturarVoucherGrupoDesdeTab($input: FacturarVoucherGrupoInput!) {
    facturarVoucherGrupo(input: $input) {
      estadoFiscal { estado codigo requestId diagnostico }
      id estado error numeroDeFacturaAfip total fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId CAE CAEFchVto
      saldoAntesFactura montoSugeridoAlEmitir montoExceso saldoDespuesFactura fechaCorteSaldo
      detalleFacturacion {
        idGrupo idSede nombreGrupo nombreSede fechaCorte generadoEn
        resumen {
          totalAplicado totalFacturado pendienteFacturar saldoFacturacion estadoSaldo montoSaldo
        }
        pedidos {
          idPedido fechaPedido fechaCierrePedido estadoPedido totalPedido
          cliente { id nombre apellido dni }
          consumos {
			idVenta idVoucher tipoVoucher cantidadUtilizada montoDescuento totalVenta
          }
        }
		facturas {
			id fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId estado total conceptoTexto clienteSnapshot
			clienteFacturado cuitClienteFacturado
			saldoAntesFactura montoSugeridoAlEmitir montoExceso saldoDespuesFactura
			fechaCorteSaldo
			numeroDeFacturaAfip CAE CAEFchVto error createdAt
          clienteFacturacion { id tipoCliente nombre apellido razonSocial cuit }
          usuarioFactura { id nombre apellido }
        }
		facturaObjetivo {
		  id fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId estado total numeroDeFacturaAfip CAE
		  saldoAntesFactura montoSugeridoAlEmitir montoExceso saldoDespuesFactura
		  fechaCorteSaldo
		}
		controlHistorico {
		  reconstruccionExacta consistente advertencia fechaCorteDesde fechaCorteHasta
		  saldoArrastrado descuentosPeriodo facturasIntermedias saldoEsperadoAntes
		  saldoRegistradoAntes diferenciaControl pedidosSinFechaCierre fuenteSaldoAnterior
		}
      }
    }
  }
`;

const FACTURA_HISTORICAL_DETAIL_QUERY = gql`
  query detalleHistoricoVoucherGrupoFactura($idFactura: ID!) {
    voucherGrupoFacturaDetalleHistorico(idFactura: $idFactura) {
      idGrupo idSede nombreGrupo nombreSede fechaCorte generadoEn
      resumen {
        idGrupo idSede tipoGrupo totalMontoAsignado totalMontoUtilizado porcentajeMontoUtilizado
        totalUnidadesAsignadas totalUnidadesUtilizadas porcentajeUnidadesUtilizadas
        totalAplicado totalFacturado pendienteFacturar saldoFacturacion estadoSaldo montoSaldo
        ultimaFactura
      }
      pedidos {
        idPedido fechaPedido fechaCierrePedido estadoPedido totalPedido
        cliente { id nombre apellido dni }
        consumos {
          idVenta idVoucher tipoVoucher cantidadUtilizada montoDescuento totalVenta
        }
      }
      facturas {
        id fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId estado total conceptoTexto clienteSnapshot
        clienteFacturado cuitClienteFacturado
        saldoAntesFactura montoSugeridoAlEmitir montoExceso saldoDespuesFactura fechaCorteSaldo
        numeroDeFacturaAfip CAE CAEFchVto error createdAt
        clienteFacturacion { id tipoCliente nombre apellido razonSocial cuit }
        usuarioFactura { id nombre apellido }
      }
      facturaObjetivo {
        id fechaFactura tipoFactura actividadCodigo tratamientoIva alicuotaIvaId estado total conceptoTexto clienteSnapshot
        clienteFacturado cuitClienteFacturado numeroDeFacturaAfip CAE CAEFchVto createdAt
        saldoAntesFactura montoSugeridoAlEmitir montoExceso saldoDespuesFactura fechaCorteSaldo
        usuarioFactura { id nombre apellido }
      }
      controlHistorico {
        reconstruccionExacta consistente advertencia fechaCorteDesde fechaCorteHasta
        saldoArrastrado descuentosPeriodo facturasIntermedias saldoEsperadoAntes
        saldoRegistradoAntes diferenciaControl pedidosSinFechaCierre fuenteSaldoAnterior
      }
    }
  }
`;

const money = (value) => `$ ${formatMoneyPunto(Number(value || 0))}`;
const signedMoney = (value) => {
	const amount = Number(value || 0);
	return `${amount < 0 ? "−" : ""}${money(Math.abs(amount))}`;
};
const formatInvoiceEmissionDate = (factura) => {
	const emissionDate = factura?.fechaFactura || factura?.createdAt;
	return emissionDate && dayjs(emissionDate).isValid()
		? dayjs(emissionDate).format("DD/MM/YYYY")
		: "Sin fecha";
};
const invoiceResult = (comparison) => {
	if (!comparison) return { label: "Sin datos", color: "#64748b" };
	const amount = Math.abs(Number(comparison.resultado || 0));
	if (comparison.estadoResultado === "A_FAVOR") {
		return { label: `${money(amount)} a favor`, color: "#b45309" };
	}
	if (comparison.estadoResultado === "PENDIENTE") {
		return { label: `${money(amount)} pendiente`, color: "#6d28d9" };
	}
	return { label: `${money(0)} · Saldado`, color: "#047857" };
};
const INVOICE_HISTORY_PAGE_SIZE = 5;
const INVOICE_HISTORY_GRID_COLUMNS = "minmax(185px, 1.45fr) minmax(185px, 1.35fr) repeat(2, minmax(115px, .9fr)) repeat(2, minmax(100px, .78fr)) minmax(135px, 1fr) 76px";
const INVOICE_HISTORY_COLUMNS = [
	"Factura",
	"Fecha emisión",
	"Saldo arrastrado",
	"Nuevos consumos",
	"Sugerido",
	"Facturado",
	"Resultado",
	"Acciones",
];
const clientName = (cliente) => cliente?.razonSocial
	|| [cliente?.apellido, cliente?.nombre].filter(Boolean).join(", ")
	|| "Cliente sin nombre";
const invoiceUserName = (usuario) => [usuario?.nombre, usuario?.apellido].filter(Boolean).join(" ");
const percentage = (value) => `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })} %`;

const BILLING_FIELD_LABEL_SX = {
	mb: 0.75,
	color: "#253451",
	fontSize: "0.8rem",
	fontWeight: 700,
	lineHeight: 1.25,
};

const BILLING_CONTROL_SX = {
	height: 42,
	boxSizing: "border-box",
	backgroundColor: "#fff",
	borderRadius: "10px",
	boxShadow: "0 4px 12px rgba(37, 52, 81, 0.08)",
	color: "#344563",
	fontSize: "0.875rem",
	fontWeight: 500,
	transition: "border-color 200ms ease, box-shadow 200ms ease",
	"& .MuiOutlinedInput-notchedOutline": { borderColor: "#d5deea" },
	"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#aebdd1" },
	"&.Mui-focused": { boxShadow: "0 0 0 3px rgba(52, 99, 206, 0.12), 0 4px 12px rgba(37, 52, 81, 0.08)" },
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3463ce", borderWidth: 1 },
	"&.Mui-disabled": { backgroundColor: "#f1f5f9", boxShadow: "none" },
	"& .MuiSelect-select": { display: "flex", alignItems: "center", minWidth: 0, py: 0 },
	"& .MuiSelect-icon": { right: 12, color: "#253451" },
};

const BILLING_SOFT_INPUT_SX = {
	display: "flex !important",
	alignItems: "center",
	minHeight: 42,
	boxSizing: "border-box",
	backgroundColor: "#fff",
	borderRadius: "10px",
	boxShadow: "0 4px 12px rgba(37, 52, 81, 0.08)",
	transition: "border-color 200ms ease, box-shadow 200ms ease",
	"&.Mui-focused": {
		borderColor: "#3463ce",
		boxShadow: "0 0 0 3px rgba(52, 99, 206, 0.12), 0 4px 12px rgba(37, 52, 81, 0.08)",
	},
	"&.Mui-disabled": { backgroundColor: "#f1f5f9 !important", boxShadow: "none" },
	"& .MuiInputBase-input": {
		width: "100% !important",
		minWidth: 0,
		color: "#344563",
		fontSize: "0.875rem !important",
		fontWeight: 500,
		lineHeight: "1.4 !important",
	},
	"& .MuiInputBase-input::placeholder": { color: "#8290a8 !important", opacity: "1 !important" },
	"& .MuiInputAdornment-root": { mr: 0.75, color: "#8295b7" },
	"& .MuiInputAdornment-root .MuiIcon-root": { fontSize: "1.1rem" },
};

const BILLING_AUTOCOMPLETE_INPUT_SX = {
	...BILLING_SOFT_INPUT_SX,
	minHeight: 40,
	boxShadow: "none",
	"&.Mui-focused": { borderColor: "transparent", boxShadow: "none" },
};

const BILLING_AUTOCOMPLETE_SX = {
	"& .MuiAutocomplete-root > div": {
		minHeight: 42,
		borderRadius: "10px",
		boxShadow: "0 4px 12px rgba(37, 52, 81, 0.08)",
		transition: "border-color 200ms ease, box-shadow 200ms ease",
	},
	"& .MuiAutocomplete-root > div:focus-within": {
		borderColor: "#3463ce",
		boxShadow: "0 0 0 3px rgba(52, 99, 206, 0.12), 0 4px 12px rgba(37, 52, 81, 0.08)",
	},
	"& .MuiAutocomplete-inputRoot": {
		display: "flex !important",
		alignItems: "center",
		flexWrap: "nowrap !important",
		minWidth: 0,
		pr: "38px !important",
	},
	"& .MuiAutocomplete-input": {
		width: "100% !important",
		minWidth: "0 !important",
	},
	"& .MuiAutocomplete-endAdornment": { right: 8 },
};

const BILLING_HELPER_SX = {
	mt: 0.75,
	fontSize: "0.72rem",
	lineHeight: 1.35,
};

// Valor calculado o fijo que se muestra con la forma de un campo, sin ser editable.
const BILLING_READONLY_SX = {
	minHeight: 42,
	display: "flex",
	alignItems: "center",
	gap: 1,
	px: 1.5,
	boxSizing: "border-box",
	borderRadius: "10px",
	border: "1px dashed #c7d2e2",
	backgroundColor: "#f8fafc",
	color: "#344563",
	fontSize: "0.875rem",
	"& .MuiIcon-root": { fontSize: "1.1rem", color: "#8295b7" },
};

const BillingSection = ({ number, title, description, children, last = false }) => (
	<SoftBox
		component="section"
		aria-labelledby={`voucher-factura-seccion-${number}`}
		sx={{
			display: "grid",
			gridTemplateColumns: "32px minmax(0, 1fr)",
			columnGap: { xs: 1.1, sm: 1.5 },
			py: 2,
			borderBottom: last ? "none" : "1px solid #e5eaf1",
		}}
	>
		<SoftBox
			aria-hidden="true"
			sx={{
				width: 30,
				height: 30,
				borderRadius: "50%",
				display: "grid",
				placeItems: "center",
				backgroundColor: "#eef2ff",
				color: "#3463ce",
				fontWeight: 700,
				fontSize: "0.85rem",
			}}
		>
			{number}
		</SoftBox>
		<SoftBox minWidth={0}>
			<SoftTypography
				id={`voucher-factura-seccion-${number}`}
				variant="button"
				fontWeight="bold"
				display="block"
				sx={{ color: "#253451", fontSize: "0.92rem", lineHeight: 1.3 }}
			>
				{title}
			</SoftTypography>
			<SoftTypography variant="caption" display="block" sx={{ mt: 0.25, mb: 1.5, color: "#64748b", fontSize: "0.74rem", lineHeight: 1.35 }}>
				{description}
			</SoftTypography>
			{children}
		</SoftBox>
	</SoftBox>
);

const SummaryIcon = ({ icon, color }) => (
	<SoftBox
		sx={{
			width: 46,
			height: 46,
			minWidth: 46,
			borderRadius: "11px",
			display: "grid",
			placeItems: "center",
			backgroundColor: `${color}12`,
		}}
	>
		<Icon aria-hidden="true" sx={{ color, fontSize: "1.45rem" }}>{icon}</Icon>
	</SoftBox>
);

const UsageSummaryCard = ({ items }) => (
	<Card
		sx={{
			minWidth: 0,
			minHeight: 112,
			alignSelf: items.length > 1 ? "stretch" : "start",
			display: "flex",
			flexDirection: "column",
			justifyContent: "flex-start",
			px: { xs: 1.4, sm: 1.75 },
			py: 0.75,
			boxShadow: "0 3px 10px rgba(37, 52, 81, 0.07)",
			border: "1px solid #dbe3ee",
			borderRadius: "12px",
			backgroundColor: "#fff",
		}}
	>
		{items.map((item, index) => (
			<SoftBox
				key={item.key}
				display="grid"
				alignItems="center"
				sx={{
					gridTemplateColumns: "46px minmax(0, 1fr) auto",
					columnGap: { xs: 1.1, sm: 1.4 },
					minHeight: 78,
					py: 0.9,
					borderBottom: index === items.length - 1 ? "none" : "1px solid #e5eaf1",
				}}
			>
				<SummaryIcon icon={item.icon} color={item.color} />
				<SoftBox minWidth={0} flex={1}>
					<SoftTypography variant="caption" fontWeight="bold" display="block" sx={{ color: item.color, fontSize: "0.7rem", lineHeight: 1.2 }}>
						{item.eyebrow}
					</SoftTypography>
					<SoftTypography variant="button" display="block" sx={{ mt: 0.45, color: "#253451", fontSize: "0.78rem", fontWeight: 500, lineHeight: 1.3 }}>
						{item.title}
					</SoftTypography>
				</SoftBox>
				<SoftBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.65} minWidth={0}>
					<SoftBox sx={{ px: 1, py: 0.45, border: `1px solid ${item.color}2b`, borderRadius: "8px", backgroundColor: `${item.color}08`, color: item.color, fontSize: "0.75rem", fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
						{item.percentage}
					</SoftBox>
					<SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#253451", fontSize: "1rem", lineHeight: 1.15, textAlign: "right", whiteSpace: "nowrap" }}>
						{item.value}
					</SoftTypography>
				</SoftBox>
			</SoftBox>
		))}
	</Card>
);

const FinancialSummaryCard = ({ rows }) => (
	<Card sx={{ minWidth: 0, height: "100%", px: { xs: 1.4, sm: 1.75 }, py: 0.75, boxShadow: "0 3px 10px rgba(37, 52, 81, 0.07)", border: "1px solid #dbe3ee", borderRadius: "12px", backgroundColor: "#fff" }}>
		{rows.map((row, index) => (
			<SoftBox key={row.key} display="flex" alignItems="center" gap={{ xs: 1.1, sm: 1.4 }} sx={{ minHeight: 54, py: 0.7, borderBottom: index === rows.length - 1 ? "none" : "1px solid #e5eaf1" }}>
				<SummaryIcon icon={row.icon} color={row.color} />
				<SoftBox minWidth={0} flex={1}>
					<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#253451", fontSize: "0.82rem", lineHeight: 1.25 }}>{row.title}</SoftTypography>
					<SoftTypography variant="caption" color="secondary" display="block" sx={{ mt: 0.2, fontSize: "0.68rem", lineHeight: 1.25 }}>{row.detail}</SoftTypography>
				</SoftBox>
				<SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#253451", fontSize: "1rem", lineHeight: 1.15, whiteSpace: "nowrap" }}>{row.value}</SoftTypography>
			</SoftBox>
		))}
	</Card>
);

const LastInvoiceCard = ({ date, user }) => (
	<Card sx={{ minWidth: 0, minHeight: 112, px: { xs: 1.4, sm: 1.75 }, py: 1.25, display: "grid", gridTemplateColumns: "46px minmax(0, 1fr) auto", alignItems: "center", columnGap: { xs: 1.1, sm: 1.4 }, boxShadow: "0 3px 10px rgba(37, 52, 81, 0.07)", border: "1px solid #dbe3ee", borderRadius: "12px", backgroundColor: "#fff" }}>
		<SummaryIcon icon="event_available" color="#0284c7" />
		<SoftBox minWidth={0}>
			<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#0284c7", fontSize: "0.78rem", lineHeight: 1.2 }}>Última factura emitida</SoftTypography>
			<SoftTypography variant="h5" fontWeight="bold" display="block" sx={{ mt: 0.45, color: "#253451", fontSize: "1.08rem", lineHeight: 1.2 }}>
				{date ? dayjs(date).format("DD/MM/YYYY") : "Sin facturas"}
			</SoftTypography>
			<SoftTypography variant="caption" color="secondary" display="block" sx={{ mt: 0.55, fontSize: "0.68rem", lineHeight: 1.25 }}>
				{date ? `Generada${user ? ` por ${user}` : ""}` : "Aún no hay emisiones exitosas"}
			</SoftTypography>
		</SoftBox>
		{date ? (
			<SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.55, border: "1px solid rgba(5,150,105,.22)", borderRadius: "8px", backgroundColor: "rgba(5,150,105,.07)", color: "#059669", fontSize: "0.68rem", fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
				<Icon aria-hidden="true" sx={{ fontSize: "1rem" }}>check_circle_outline</Icon>
				Emitida
			</SoftBox>
		) : null}
	</Card>
);

const InvoiceConfirmationRow = ({ icon, label, value, highlight = false, last = false }) => (
	<SoftBox
		display="grid"
		alignItems="center"
		sx={{
			gridTemplateColumns: "42px minmax(0, 1fr)",
			columnGap: { xs: 1.25, sm: 1.75 },
			py: 0.8,
			borderBottom: last ? "none" : "1px dashed #e2e8f0",
		}}
	>
		<SoftBox
			display="grid"
			sx={{
				width: 40,
				height: 40,
				placeItems: "center",
				borderRadius: "10px",
				backgroundColor: "#f1f5f9",
				color: "#253451",
			}}
		>
			<Icon aria-hidden="true" sx={{ fontSize: "1.25rem" }}>{icon}</Icon>
		</SoftBox>
		<SoftBox display="flex" alignItems="baseline" flexWrap="wrap" columnGap={1} rowGap={0.2} minWidth={0}>
			<SoftTypography variant="button" fontWeight="bold" sx={{ color: "#253451", fontSize: "0.92rem" }}>
				{label}:
			</SoftTypography>
			<SoftTypography
				variant="button"
				sx={{
					color: highlight ? "#08a958" : "#344563",
					fontSize: "0.92rem",
					fontWeight: highlight ? 700 : 500,
					overflowWrap: "anywhere",
				}}
			>
				{value}
			</SoftTypography>
		</SoftBox>
	</SoftBox>
);

const InvoiceHistoryCell = ({ label, children, sx = {} }) => (
	<SoftBox role="cell" minWidth={0} sx={sx}>
		<SoftTypography
			variant="caption"
			fontWeight="bold"
			display={{ xs: "block", xxl: "none" }}
			sx={{ mb: 0.35, color: "#64748b", fontSize: "0.65rem", lineHeight: 1.2 }}
		>
			{label}
		</SoftTypography>
		{children}
	</SoftBox>
);

export default function VoucherFacturacionTab({ grupo, puedeFacturar, puedeAdministrarClientes }) {
	const { handleSnackbar } = useContext(MessageManager);
	const addClientButtonRef = useRef(null);
	const emisionEnCursoRef = useRef(false);
	const [idSede, setIdSede] = useState(grupo?.idSede ? String(grupo.idSede) : "");
	const [cliente, setCliente] = useState(grupo?.clienteFacturacion || null);
	const [actividadCodigo, setActividadCodigo] = useState("");
	const [opcionIvaClave, setOpcionIvaClave] = useState("");
	const [falloEmision, setFalloEmision] = useState(null);
	const [tipoFactura, setTipoFactura] = useState("");
	const [montoFactura, setMontoFactura] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [clientDialogOpen, setClientDialogOpen] = useState(false);
	const [selectedFactura, setSelectedFactura] = useState(null);
	const [downloadingFacturaId, setDownloadingFacturaId] = useState(null);
	const [facturasSearch, setFacturasSearch] = useState("");
	const [debouncedFacturasSearch, setDebouncedFacturasSearch] = useState("");
	const [facturasPage, setFacturasPage] = useState(1);
	const [facturasOrder, setFacturasOrder] = useState("DESC");
	const [facturasEstado, setFacturasEstado] = useState("");
	const [facturaResaltadaId, setFacturaResaltadaId] = useState(null);
	// Reintento: nueva decision sobre la misma factura con las correcciones admitidas.
	const [reintentoFactura, setReintentoFactura] = useState(null);
	const [reintento, setReintento] = useState(correccionesIniciales(null));
	const [descarteFactura, setDescarteFactura] = useState(null);
	const [motivoDescarte, setMotivoDescarte] = useState("");
	// "Ver factura": se pide esa factura puntual aunque no este en la pagina visible.
	const [facturaFocoId, setFacturaFocoId] = useState(null);
	const [incidenteFactura, setIncidenteFactura] = useState(null);
	const [motivoIncidente, setMotivoIncidente] = useState("");
	const historyRef = useRef(null);

	const { data: sedesData } = useQuery(SEDES_QUERY);
	const { data: catalogoFiscalData, error: catalogoFiscalError } = useQuery(CATALOGO_FISCAL_QUERY, {
		variables: { idSede },
		skip: !idSede,
	});
	const { data: clientesData, refetch: refetchClientes } = useQuery(CLIENTES_QUERY, {
		variables: { page: 1, limit: 100 },
	});
	const canQuery = Boolean(grupo?.id && idSede);
	const queryVariables = { idGrupo: grupo?.id, idSede };
	const {
		data: resumenData,
		loading: loadingResumen,
		error: resumenError,
		refetch: refetchResumen,
	} = useQuery(RESUMEN_QUERY, {
		variables: queryVariables,
		skip: !canQuery,
		fetchPolicy: "network-only",
	});
	const {
		data: facturasData,
		loading: loadingFacturas,
		refetch: refetchFacturas,
	} = useQuery(FACTURAS_QUERY, {
		variables: {
			idGrupo: grupo?.id,
			idSede,
			search: debouncedFacturasSearch || null,
			page: facturasPage,
			limit: INVOICE_HISTORY_PAGE_SIZE,
			order: facturasOrder,
			estado: facturasEstado || null,
			idFactura: facturaFocoId || null,
		},
		skip: !grupo?.id || !idSede,
		fetchPolicy: "network-only",
	});
	const [facturar, { loading: facturando }] = useMutation(FACTURAR_MUTATION);
	const [continuarFactura] = useMutation(CONTINUAR_FACTURA_MUTATION);
	const [reintentarFactura, { loading: reintentando }] = useMutation(REINTENTAR_FACTURA_MUTATION);
	const [descartarFactura, { loading: descartando }] = useMutation(DESCARTAR_FACTURA_MUTATION);
	const [resolverIncidente, { loading: resolviendoIncidente }] = useMutation(RESOLVER_INCIDENTE_MUTATION);
	const [verificandoFacturaId, setVerificandoFacturaId] = useState(null);
	const [loadHistoricalInvoiceDetail] = useLazyQuery(FACTURA_HISTORICAL_DETAIL_QUERY, {
		fetchPolicy: "network-only",
	});
	const [loadCurrentBillingDetail, { loading: downloadingCurrentDetail }] = useLazyQuery(
		FACTURACION_DETAIL_QUERY,
		{ fetchPolicy: "network-only" }
	);
	const sedes = sedesData?.sedes || [];
	const catalogoFiscal = catalogoFiscalData?.voucherCatalogoFiscal;
	const actividades = catalogoFiscal?.actividades || [];
	const advertenciasEmisor = catalogoFiscal?.advertencias || [];
	const actividad = actividades.find((item) => item.codigo === actividadCodigo) || null;
	const opcionesIva = catalogoFiscal?.opcionesIva || [];
	const opcionIva = opcionesIva.find((item) => item.clave === opcionIvaClave) || null;
	const opcionIvaSugerida = opcionesIva.find((item) => item.clave === actividad?.opcionIvaSugerida) || null;
	const clientes = clientesData?.voucherClientes?.rows || [];
	const resumen = resumenData?.voucherGrupoFacturacionResumen;
	// Una intencion pendiente (ERROR, PROCESANDO o incidente) impide emitir otra factura.
	const bloqueoEmision = resumen?.bloqueoEmision || null;
	const bloqueoDescripcion = describirBloqueo(bloqueoEmision, { puedeFacturar });
	const facturas = facturasData?.voucherGrupoFacturas?.rows || [];
	const facturasPagination = facturasData?.voucherGrupoFacturas?.pagination
		|| { total: 0, pages: 0, page: 1, limit: INVOICE_HISTORY_PAGE_SIZE };
	const pendienteFacturar = resumen?.pendienteFacturar;
	const saldoFacturacionRaw = resumen?.saldoFacturacion ?? pendienteFacturar;
	const saldoFacturacion = Number(saldoFacturacionRaw || 0);
	const montoSugerido = Math.max(saldoFacturacion, 0);
	const facturaDescription = `Consumo de vouchers ${String(grupo?.nombre || "").trim() || "-"}`;

	useEffect(() => {
		if (grupo?.idSede) setIdSede(String(grupo.idSede));
		else if (!idSede && sedes.length) setIdSede(String(sedes[0].id));
	}, [grupo?.idSede, idSede, sedes]);

	useEffect(() => {
		setActividadCodigo("");
		setOpcionIvaClave("");
	}, [idSede]);

	useEffect(() => {
		if (cliente) return;
		const sugerido = resumen?.clienteSugerido || grupo?.clienteFacturacion;
		if (sugerido) setCliente(sugerido);
	}, [cliente, grupo?.clienteFacturacion, resumen?.clienteSugerido]);

	useEffect(() => {
		if (saldoFacturacionRaw === undefined || saldoFacturacionRaw === null) return;
		setMontoFactura(montoSugerido > 0 ? Number(montoSugerido.toFixed(2)) : null);
	}, [montoSugerido, saldoFacturacionRaw]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setDebouncedFacturasSearch(facturasSearch.trim());
			setFacturasPage(1);
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [facturasSearch]);

	useEffect(() => {
		setFacturasSearch("");
		setDebouncedFacturasSearch("");
		setFacturasPage(1);
		setFacturasEstado("");
		setFacturaResaltadaId(null);
		setFacturaFocoId(null);
	}, [grupo?.id, idSede]);

	const usageSummaryItems = useMemo(() => {
		if (!resumen) return [];
		const result = [];
		if (["MONTO", "MIXTO"].includes(resumen.tipoGrupo)) {
			result.push({
				key: "amount",
				icon: "confirmation_number",
				eyebrow: "vouchers monto",
				title: "Consumido / Monto total asignado",
				value: `${money(resumen.totalMontoUtilizado)} / ${money(resumen.totalMontoAsignado)}`,
				percentage: percentage(resumen.porcentajeMontoUtilizado),
				color: "#2563eb",
			});
		}
		if (["PRODUCTO", "CATEGORIA", "MIXTO"].includes(resumen.tipoGrupo)) {
			result.push({
				key: "units",
				icon: "confirmation_number",
				eyebrow: "vouchers especie",
				title: "Consumida / Cantidad total asignada",
				value: `${Number(resumen.totalUnidadesUtilizadas).toLocaleString("es-AR")} /${Number(resumen.totalUnidadesAsignadas).toLocaleString("es-AR")}`,
				percentage: percentage(resumen.porcentajeUnidadesUtilizadas),
				color: "#059669",
			});
		}
		return result;
	}, [resumen]);

	const financialSummaryRows = useMemo(() => {
		if (!resumen) return [];
		const saldo = Number(resumen.saldoFacturacion ?? resumen.pendienteFacturar ?? 0);
		const saldoPresentation = saldo > 0
			? { title: "Pendiente de facturar", detail: "saldo disponible a la fecha", color: "#7c3aed" }
			: saldo < 0
				? { title: "Saldo a favor", detail: "importe facturado por encima de lo aplicado", color: "#d97706" }
				: { title: "Saldado", detail: "sin diferencias a la fecha", color: "#64748b" };
		return [
			{
				key: "applied",
				icon: "paid",
				title: "Total en pesos",
				value: money(resumen.totalAplicado),
				detail: "aplicado en vouchers",
				color: "#0f9f92",
			},
			{
				key: "invoiced",
				icon: "receipt_long",
				title: "Total facturado",
				value: money(resumen.totalFacturado),
				detail: "facturas emitidas a la fecha",
				color: "#059669",
			},
			{
				key: "pending",
				icon: "pending_actions",
				title: saldoPresentation.title,
				value: money(Math.abs(saldo)),
				detail: saldoPresentation.detail,
				color: saldoPresentation.color,
			},
		];
	}, [resumen]);
	const selectedSede = sedes.find((item) => Number(item.id) === Number(idSede)) || grupo?.sede;
	const sedeFiscalCompleta = Boolean(
		selectedSede?.cuit && selectedSede?.ptoVenta
	);
	// La actividad solo sugiere el IVA: el usuario puede elegir cualquier opcion del emisor.
	// pendiente: texto corto para la lista de "Falta"; mensaje: aviso junto a los campos.
	const validacionFiscal = catalogoFiscalError
		? { pendiente: "configurar las reglas fiscales de la sede", mensaje: catalogoFiscalError.message }
		: !actividad
			? { pendiente: "elegir la actividad", mensaje: "" }
			: !opcionIva
				? { pendiente: "elegir el IVA", mensaje: "" }
				: { pendiente: "", mensaje: "" };
	const montoNumber = Number(montoFactura);
	const montoRedondeado = Math.round(montoNumber * 100) / 100;
	const montoTieneMasDeDosDecimales = Number.isFinite(montoNumber)
		&& Math.abs(montoNumber - montoRedondeado) > 0.000001;
	const montoSuperaSugerido = Number.isFinite(montoNumber) && montoNumber > montoSugerido;
	const montoExceso = montoSuperaSugerido
		? Math.round((montoNumber - montoSugerido) * 100) / 100
		: 0;
	const saldoResultante = Math.round((saldoFacturacion - montoRedondeado) * 100) / 100;
	const montoError = !montoFactura || !Number.isFinite(montoNumber) || montoNumber <= 0
		? "Ingrese un monto mayor a cero."
		: montoTieneMasDeDosDecimales
			? "El monto admite como máximo dos decimales."
			: "";
	const mostrarMontoError = Boolean(montoError) && montoFactura !== null && montoFactura !== "";
	const pendientes = [
		!resumen && "cargar el resumen del grupo",
		bloqueoEmision && `resolver la factura #${bloqueoEmision.idFactura}`,
		!cliente?.id && "elegir el cliente",
		selectedSede && !sedeFiscalCompleta && "completar los datos fiscales de la sede",
		!tipoFactura && "elegir el tipo de factura",
		validacionFiscal.pendiente,
		montoError && "ingresar un monto válido",
	].filter(Boolean);
	const canPrepareInvoice = Boolean(puedeFacturar && idSede && !pendientes.length && !facturando);
	const canInvoice = canPrepareInvoice;
	const importesPreview = previewImportes({ total: montoRedondeado, tipoFactura, opcionIva });
	const saldoLabel = saldoFacturacion > 0
		? "Pendiente de facturar"
		: saldoFacturacion < 0 ? "Saldo a favor" : "Saldado";
	const saldoDisplay = money(Math.abs(saldoFacturacion));

	const handleActividadChange = (codigo) => {
		setActividadCodigo(codigo);
	};

	const openInvoiceConfirmation = () => {
		if (!puedeFacturar) return;
		setConfirmOpen(true);
	};

	const handleInvoice = async (descargarExcel = false, solicitudAnterior = null) => {
		if (!puedeFacturar || emisionEnCursoRef.current) return;
		emisionEnCursoRef.current = true;
		// El reintento del diálogo conserva exactamente la solicitud rechazada,
		// incluida la opción de descargar Excel, aunque cambie un dato sugerido.
		const input = solicitudAnterior || {
			idGrupo: grupo.id,
			idSede,
			idVoucherCliente: cliente.id,
			tipoFactura,
			monto: montoRedondeado,
			actividadCodigo,
			tratamientoIva: opcionIva.tratamientoIva,
			alicuotaIvaId: opcionIva.alicuotaIvaId,
			incluirDetalleExcel: descargarExcel,
		};
		try {
			const response = await facturar({
				variables: { input },
			});
			const result = response?.data?.facturarVoucherGrupo;
			setConfirmOpen(false);
			let excelError = null;
			if (input.incluirDetalleExcel && result?.estado === "EMITIDA") {
				try {
					downloadVoucherFacturacionExcel(result.detalleFacturacion, result);
				} catch (error) {
					excelError = error;
				}
			}
			await Promise.all([refetchResumen(), refetchFacturas()]);
			if (result?.estado === "EMITIDA") {
				setFalloEmision(null);
				if (input.incluirDetalleExcel && excelError) {
					handleSnackbar(
						`Factura ${tipoFactura} emitida, pero no se pudo descargar el Excel: ${excelError.message}`,
						"warning"
					);
				} else if (input.incluirDetalleExcel) {
					handleSnackbar(`Factura ${tipoFactura} emitida y Excel descargado correctamente`, "success");
				} else {
					handleSnackbar(`Factura ${tipoFactura} emitida correctamente`, "success");
				}
			} else if (result?.estado === "PROCESANDO") {
				setFalloEmision({ mensaje: result.estadoFiscal?.diagnostico?.causa || result.error || "ARCA todavía no confirmó la factura", diagnostico: result.estadoFiscal?.diagnostico, estado: result.estado, idFactura: result.id });
			} else {
				setFalloEmision({ mensaje: result?.estadoFiscal?.diagnostico?.causa || result?.error || "No se pudo emitir la factura", diagnostico: result?.estadoFiscal?.diagnostico, estado: result?.estado, idFactura: result?.id });
			}
		} catch (error) {
			setConfirmOpen(false);
			if (facturaBloqueanteDeError(error)) {
				// Otra intencion quedo pendiente: el resumen muestra el acceso a esa factura.
				await refetchResumen();
				handleSnackbar("Hay una factura pendiente de resolver antes de emitir otra.", "warning");
				return;
			}
			const diagnostico = diagnosticoErrorFacturacion(error);
			setFalloEmision({
				mensaje: diagnostico?.causa || mensajeErrorFacturacion(error),
				diagnostico,
				solicitud: esValidacionFiscalRechazada(error) ? input : null,
				intentarMasTarde: esFalloTemporalPrevio(error),
			});
		} finally {
			emisionEnCursoRef.current = false;
		}
	};

	const descartarIntento = () => {
		if (emisionEnCursoRef.current) return;
		setFalloEmision(null);
		setTipoFactura("");
		setActividadCodigo("");
		setOpcionIvaClave("");
		setMontoFactura(montoSugerido > 0 ? Number(montoSugerido.toFixed(2)) : null);
		setCliente(resumen?.clienteSugerido || grupo?.clienteFacturacion || null);
	};

	const modificarIntento = () => {
		setFalloEmision(null);
		setTimeout(() => {
			const control = document.getElementById("voucher-factura-tipo");
			control?.scrollIntoView?.({ behavior: "smooth", block: "center" });
			control?.focus({ preventScroll: true });
		}, 0);
	};

	// Lleva el historial a la factura que bloquea la emision. Pide solo esa
	// factura (sin filtro de estado): sigue visible aunque cambie de estado.
	const irAFactura = (idFactura) => {
		setFacturasSearch("");
		setDebouncedFacturasSearch("");
		setFacturasEstado("");
		setFacturasPage(1);
		setFacturaFocoId(String(idFactura));
		setFacturaResaltadaId(String(idFactura));
		setTimeout(() => historyRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 0);
	};

	const abrirReintento = (factura) => {
		setReintentoFactura(factura);
		setReintento(correccionesIniciales(factura));
	};

	const reintentoActividad = actividades.find((item) => item.codigo === reintento.actividadCodigo) || null;
	const reintentoOpcionIva = opcionesIva.find((item) => item.clave === reintento.opcionIvaClave) || null;
	const reintentoPendiente = !reintento.tipoFactura
		? "Elegí el tipo de factura."
		: !reintentoActividad
			? "Elegí la actividad."
			: !reintentoOpcionIva ? "Elegí el IVA." : "";

	const handleReintentar = async () => {
		if (!puedeFacturar || !reintentoFactura || reintentoPendiente) return;
		try {
			const response = await reintentarFactura({
				variables: {
					input: {
						idFactura: reintentoFactura.id,
						correcciones: buildCorrecciones({
							tipoFactura: reintento.tipoFactura,
							actividadCodigo: reintento.actividadCodigo,
							opcionIva: reintentoOpcionIva,
						}),
					},
				},
			});
			const result = response?.data?.reintentarFacturaVoucher;
			setReintentoFactura(null);
			await Promise.all([refetchResumen(), refetchFacturas()]);
			if (result?.estado === "EMITIDA") {
				handleSnackbar(`Factura ${result.tipoFactura} N° ${result.numeroDeFacturaAfip} emitida`, "success");
			} else if (result?.estado === "PROCESANDO") {
				handleSnackbar("La factura quedó pendiente de confirmación en ARCA.", "warning");
			} else {
				setFalloEmision({ mensaje: result?.estadoFiscal?.diagnostico?.causa || result?.error || "No se pudo emitir la factura", diagnostico: result?.estadoFiscal?.diagnostico, estado: result?.estado, idFactura: result?.id });
			}
		} catch (error) {
			handleSnackbar(mensajeErrorFacturacion(error, "No se pudo reintentar la factura"), "error");
		}
	};

	const motivoDescarteError = validarMotivoDescarte(motivoDescarte);

	const handleDescartar = async () => {
		if (!puedeFacturar || !descarteFactura || motivoDescarteError) return;
		try {
			await descartarFactura({
				variables: { input: { idFactura: descarteFactura.id, motivo: motivoDescarte.trim() } },
			});
			setDescarteFactura(null);
			setMotivoDescarte("");
			await Promise.all([refetchResumen(), refetchFacturas()]);
			handleSnackbar("Factura descartada. Ya podés emitir una nueva.", "success");
		} catch (error) {
			handleSnackbar(mensajeErrorFacturacion(error, "No se pudo descartar la factura"), "error");
		}
	};

	const motivoIncidenteError = validarMotivoDescarte(motivoIncidente);

	// Cierra los incidentes revisados: no cambia el estado ni los datos fiscales.
	const handleResolverIncidente = async () => {
		if (!puedeFacturar || !incidenteFactura || motivoIncidenteError) return;
		try {
			await resolverIncidente({
				variables: { input: { idFactura: incidenteFactura.id, motivo: motivoIncidente.trim() } },
			});
			setIncidenteFactura(null);
			setMotivoIncidente("");
			await Promise.all([refetchResumen(), refetchFacturas()]);
			handleSnackbar("Incidente marcado como revisado.", "success");
		} catch (error) {
			handleSnackbar(mensajeErrorFacturacion(error, "No se pudo marcar el incidente como revisado"), "error");
		}
	};

	const handleClientSaved = async (created) => {
		if (!puedeAdministrarClientes) return;
		await refetchClientes();
		setCliente(created);
		setTimeout(() => addClientButtonRef.current?.focus(), 0);
	};

	// Verifica una factura pendiente o con intervencion en la API de Facturacion.
	const handleVerificarFactura = async (factura) => {
		setVerificandoFacturaId(factura.id);
		try {
			const response = await continuarFactura({ variables: { idFactura: factura.id } });
			const result = response?.data?.continuarFacturaVoucher;
			await Promise.all([refetchResumen(), refetchFacturas()]);
			if (result?.estado === "EMITIDA") {
				handleSnackbar(`Factura ${result.numeroDeFacturaAfip || factura.id} confirmada por ARCA`, "success");
			} else if (result?.estadoFiscal?.requiereIntervencion) {
				handleSnackbar("La factura requiere intervención. El detalle está en el historial.", "warning");
			} else if (result?.estado === "PROCESANDO") {
				handleSnackbar("ARCA todavía no confirmó la factura; se seguirá verificando automáticamente", "info");
			} else {
				handleSnackbar(result?.error || "ARCA no autorizó la factura", "error");
			}
		} catch (error) {
			handleSnackbar(mensajeErrorFacturacion(error, "No se pudo verificar la factura"), "error");
		} finally {
			setVerificandoFacturaId(null);
		}
	};

	const handleHistoricalExcelDownload = async (factura) => {
		setDownloadingFacturaId(factura.id);
		try {
			const response = await loadHistoricalInvoiceDetail({ variables: { idFactura: factura.id } });
			const detalle = response?.data?.voucherGrupoFacturaDetalleHistorico;
			if (!detalle?.resumen) throw Error("No se recibió el detalle histórico de la factura");
			downloadVoucherFacturacionExcel(detalle, detalle.facturaObjetivo || factura);
			if (detalle.controlHistorico?.advertencia) {
				handleSnackbar(
					`Excel descargado con advertencias: ${detalle.controlHistorico.advertencia}`,
					"warning"
				);
			} else {
				handleSnackbar("Excel histórico descargado correctamente", "success");
			}
		} catch (error) {
			handleSnackbar(error?.message || "No se pudo reconstruir el Excel histórico", "error");
		} finally {
			setDownloadingFacturaId(null);
		}
	};

	const handleCurrentExcelDownload = async () => {
		try {
			const response = await loadCurrentBillingDetail({
				variables: { idGrupo: grupo.id, idSede },
			});
			const detalle = response?.data?.voucherGrupoFacturacionDetalle;
			if (!detalle?.resumen) throw Error("No se recibió el detalle acumulado de facturación");
			downloadVoucherFacturacionExcel(detalle, null);
			handleSnackbar("Excel acumulado descargado correctamente", "success");
		} catch (error) {
			handleSnackbar(error?.message || "No se pudo descargar el Excel acumulado", "error");
		}
	};

	return (
		<Card sx={{ p: 2, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
			<SoftBox
				display="flex"
				justifyContent="space-between"
				alignItems={{ xs: "flex-start", md: "center" }}
				gap={1}
				flexDirection={{ xs: "column", md: "row" }}
				mb={2}
			>
				<SoftBox>
					<SoftTypography variant="button" fontWeight="bold" display="block">
						Facturación agrupada
					</SoftTypography>
					<SoftTypography variant="caption" color="secondary">
						Saldo calculado con pedidos cerrados y sin factura individual hasta la fecha actual.
					</SoftTypography>
				</SoftBox>
				<SoftBox display="flex" alignItems="center" gap={1} sx={{ width: { xs: "100%", md: "auto" } }}>
					<Tooltip title="Descargar excel con todos los pedidos y facturas" placement="top">
						<span style={{ display: "inline-flex" }}>
							<SoftButton
								color="uproGreen"
								variant="outlined"
								circular
								iconOnly
								disabled={!canQuery || downloadingCurrentDetail}
								onClick={handleCurrentExcelDownload}
								aria-label={downloadingCurrentDetail ? "Generando Excel acumulado de facturación" : "Descargar Excel acumulado de facturación"}
								sx={{
									width: 42,
									minWidth: 42,
									height: 42,
									minHeight: 42,
									p: "7px !important",
								}}
							>
								<SoftBox component="img" src={excelIcon} alt="" sx={{ width: 26, height: 26, objectFit: "contain" }} />
							</SoftButton>
						</span>
					</Tooltip>
				</SoftBox>
			</SoftBox>

			{resumenError ? (
				<SoftTypography variant="button" color="error" display="block" mb={2}>
					{resumenError.message}
				</SoftTypography>
			) : null}
			{advertenciasEmisor.length ? (
				<SoftBox
					role="alert"
					display="flex"
					alignItems="flex-start"
					gap={0.6}
					mb={2}
					sx={{ p: 1, borderRadius: "8px", backgroundColor: "#fffbeb", color: "#92400e" }}
				>
					<Icon aria-hidden="true" sx={{ mt: "1px", fontSize: "1rem" }}>warning_amber</Icon>
					<SoftTypography variant="caption" sx={{ color: "inherit", lineHeight: 1.35 }}>
						{advertenciasEmisor.join(" · ")}
					</SoftTypography>
				</SoftBox>
			) : null}
			{loadingResumen ? <Loading /> : resumen ? (
				<SoftBox
					display="grid"
					gap={1.25}
					mb={1.5}
					aria-live="polite"
					sx={{
						gridTemplateColumns: {
							xs: "minmax(0, 1fr)",
							md: "repeat(2, minmax(0, 1fr))",
							lg: "1.12fr 1.02fr 0.9fr",
						},
						alignItems: "stretch",
					}}
				>
					<FinancialSummaryCard rows={financialSummaryRows} />
					<UsageSummaryCard items={usageSummaryItems} />
					<SoftBox sx={{ gridColumn: { xs: "auto", md: "1 / -1", lg: "auto" } }}>
						<LastInvoiceCard date={resumen.ultimaFactura} user={invoiceUserName(resumen.ultimaFacturaUsuario)} />
					</SoftBox>
				</SoftBox>
			) : !idSede ? (
				<SinDatos titulo="Seleccione una sede" mensaje="La sede es necesaria para calcular y emitir la factura." />
			) : null}

			{/* Aviso al inicio del formulario: hay una intencion pendiente en el grupo. */}
			{bloqueoDescripcion ? (
				<SoftBox
					role="alert"
					display="flex"
					alignItems={{ xs: "flex-start", sm: "center" }}
					flexDirection={{ xs: "column", sm: "row" }}
					gap={1.25}
					mb={1.5}
					sx={{ p: 1.75, borderRadius: "12px", border: "1px solid #fcd34d", backgroundColor: "#fffbeb" }}
				>
					<Icon aria-hidden="true" sx={{ color: "#b45309" }}>pending_actions</Icon>
					<SoftBox flex={1} minWidth={0}>
						<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#92400e" }}>
							{bloqueoDescripcion.titulo}: no se puede emitir otra factura
						</SoftTypography>
						<SoftTypography variant="caption" display="block" sx={{ color: "#78350f", wordBreak: "break-word" }}>
							{bloqueoDescripcion.detalle}
						</SoftTypography>
						<SoftTypography variant="caption" display="block" sx={{ color: "#78350f" }}>
							{bloqueoDescripcion.accion}
						</SoftTypography>
					</SoftBox>
					<SoftButton
						variant="outlined"
						color="warning"
						size="small"
						onClick={() => irAFactura(bloqueoEmision.idFactura)}
						sx={{ whiteSpace: "nowrap" }}
					>
						<Icon>visibility</Icon>&nbsp;Ver factura #{bloqueoEmision.idFactura}
					</SoftButton>
				</SoftBox>
			) : null}

			{puedeFacturar ? (
				<Card
					sx={{
						px: { xs: 1.4, sm: 2 },
						py: 0.25,
						mb: 1.5,
						boxShadow: "0 3px 10px rgba(37, 52, 81, 0.07)",
						border: "1px solid #dbe3ee",
						borderRadius: "12px",
						backgroundColor: "#fff",
					}}
				>
					<BillingSection
						number={1}
						title="A quién se factura"
						description="Elegí la sede, el cliente y el tipo de factura."
					>
						<Grid container spacing={1.5}>
							<Grid item xs={12} sm={6} lg={3}>
								<InputLabel id="voucher-factura-sede-label" sx={BILLING_FIELD_LABEL_SX}>Sede</InputLabel>
								<Select
									id="voucher-factura-sede"
									labelId="voucher-factura-sede-label"
									fullWidth
									value={idSede}
									disabled={Boolean(grupo?.idSede)}
									onChange={(event) => setIdSede(event.target.value)}
									startAdornment={<InputAdornment position="start"><Icon>location_on</Icon></InputAdornment>}
									sx={BILLING_CONTROL_SX}
								>
									{sedes.map((item) => (
										<MenuItem key={item.id} value={String(item.id)}>{item.nombre}</MenuItem>
									))}
								</Select>
								{selectedSede && !sedeFiscalCompleta ? (
									<SoftTypography variant="caption" color="error" display="block" sx={BILLING_HELPER_SX}>
										Configurá CUIT, punto de venta y condición IVA de la sede antes de emitir.
									</SoftTypography>
								) : null}
							</Grid>
							<Grid item xs={12} sm={6} lg={5}>
								<SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={0.75}>
									<InputLabel id="voucher-factura-cliente-label" sx={{ ...BILLING_FIELD_LABEL_SX, mb: 0 }}>
										Cliente a facturar
									</InputLabel>
									<SoftButton
										ref={addClientButtonRef}
										variant="text"
										color="uproGreen"
										size="small"
										onClick={() => puedeAdministrarClientes && setClientDialogOpen(true)}
										aria-label="Agregar cliente facturable"
										sx={{
											display: puedeAdministrarClientes ? "inline-flex" : "none",
											minWidth: "auto",
											minHeight: "auto",
											p: 0,
											fontSize: "0.75rem",
											lineHeight: 1.25,
											background: "transparent !important",
											boxShadow: "none",
											"& .MuiIcon-root": { mr: 0.25, fontSize: "1rem" },
										}}
									>
										<Icon>add</Icon>Agregar cliente
									</SoftButton>
								</SoftBox>
								<SoftBox sx={BILLING_AUTOCOMPLETE_SX}>
									<Autocomplete
										fullWidth
										options={clientes}
										value={cliente}
										isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
										getOptionLabel={(option) => `${clientName(option)} · CUIT ${option.cuit || "-"}`}
										onChange={(_, value) => setCliente(value)}
										renderInput={(params) => (
											<SoftInput
												{...params.InputProps}
												startAdornment={undefined}
												inputProps={{ ...params.inputProps, "aria-labelledby": "voucher-factura-cliente-label" }}
												placeholder="Seleccione un cliente"
												icon={{ component: "group", direction: "left" }}
												sx={BILLING_AUTOCOMPLETE_INPUT_SX}
											/>
										)}
									/>
								</SoftBox>
							</Grid>
							<Grid item xs={12} lg={4}>
								<InputLabel id="voucher-factura-tipo-label" sx={BILLING_FIELD_LABEL_SX}>Tipo de factura</InputLabel>
								<Select
									id="voucher-factura-tipo"
									labelId="voucher-factura-tipo-label"
									fullWidth
									displayEmpty
									value={tipoFactura}
									onChange={(event) => setTipoFactura(event.target.value)}
									startAdornment={<InputAdornment position="start"><Icon>receipt_long</Icon></InputAdornment>}
									sx={BILLING_CONTROL_SX}
								>
									<MenuItem value="" disabled>Seleccione</MenuItem>
									{TIPOS_FACTURA.map((tipo) => (
										<MenuItem key={tipo} value={tipo}>Factura {tipo}</MenuItem>
									))}
								</Select>
								<SoftTypography variant="caption" color="secondary" display="block" sx={BILLING_HELPER_SX}>
									Si no corresponde a la sede o al cliente, ARCA rechaza la factura y se muestra el motivo.
								</SoftTypography>
							</Grid>
						</Grid>
					</BillingSection>

					<BillingSection
						number={2}
						title="Qué se factura"
						description="La actividad sugiere el IVA; podés elegir otro."
					>
						<Grid container spacing={1.5}>
							<Grid item xs={12} lg={6}>
								<InputLabel id="voucher-factura-actividad-label" sx={BILLING_FIELD_LABEL_SX}>Actividad facturada</InputLabel>
								<Select
									id="voucher-factura-actividad"
									labelId="voucher-factura-actividad-label"
									fullWidth
									displayEmpty
									value={actividadCodigo}
									onChange={(event) => handleActividadChange(event.target.value)}
									startAdornment={<InputAdornment position="start"><Icon>work_outline</Icon></InputAdornment>}
									sx={BILLING_CONTROL_SX}
								>
									<MenuItem value="" disabled>Seleccione una actividad</MenuItem>
									{actividades.map((item) => (
										<MenuItem key={item.codigo} value={item.codigo}>{item.codigo} · {item.descripcion}</MenuItem>
									))}
								</Select>
							</Grid>
							<Grid item xs={12} sm={6} lg={3}>
								<InputLabel id="voucher-factura-iva-label" sx={BILLING_FIELD_LABEL_SX}>IVA</InputLabel>
								<Select
									id="voucher-factura-iva"
									labelId="voucher-factura-iva-label"
									fullWidth
									displayEmpty
									value={opcionIvaClave}
									onChange={(event) => setOpcionIvaClave(event.target.value)}
									startAdornment={<InputAdornment position="start"><Icon>percent</Icon></InputAdornment>}
									sx={BILLING_CONTROL_SX}
								>
									<MenuItem value="" disabled>Seleccione</MenuItem>
									{opcionesIva.map((item) => (
										<MenuItem key={item.clave} value={item.clave}>
											{item.descripcion}{item.clave === actividad?.opcionIvaSugerida ? " · sugerido" : ""}
										</MenuItem>
									))}
								</Select>
								<SoftTypography variant="caption" color="secondary" display="block" sx={BILLING_HELPER_SX}>
									{opcionIvaSugerida
										? `Sugerido para la actividad: ${opcionIvaSugerida.descripcion}.`
										: "Elegí una actividad para ver la sugerencia."}
								</SoftTypography>
							</Grid>
							{validacionFiscal.mensaje ? (
								<Grid item xs={12}>
									<SoftTypography variant="caption" color="error" display="block" sx={BILLING_HELPER_SX}>
										{validacionFiscal.mensaje}
									</SoftTypography>
								</Grid>
							) : null}
						</Grid>
					</BillingSection>

					<BillingSection
						number={3}
						title="Cuánto se factura"
						description="Se propone el saldo pendiente del grupo; podés ajustarlo."
					>
						<Grid container spacing={1.5}>
							<Grid item xs={12} sm={6} lg={4}>
								<InputLabel htmlFor="voucher-factura-monto" sx={BILLING_FIELD_LABEL_SX}>Monto a facturar</InputLabel>
								<SoftInputNumber
									id="voucher-factura-monto"
									value={montoFactura}
									onChange={(event) => {
										setMontoFactura(event.target.value);
									}}
									error={mostrarMontoError}
									inputProps={{ inputMode: "decimal", min: 0.01, step: 0.01 }}
									disabled={!resumen || facturando}
									placeholder="0,00"
									startAdornment={<InputAdornment position="start"><Icon>paid</Icon></InputAdornment>}
									sx={BILLING_SOFT_INPUT_SX}
								/>
								<SoftTypography
									variant="caption"
									color={mostrarMontoError ? "error" : montoSuperaSugerido ? "warning" : "secondary"}
									display="block"
									sx={BILLING_HELPER_SX}
								>
									{mostrarMontoError
										? montoError
										: montoSuperaSugerido
											? `Supera el sugerido en ${money(montoExceso)}.`
											: `Monto sugerido: ${money(montoSugerido)} · ${saldoLabel}: ${saldoDisplay}`}
								</SoftTypography>
							</Grid>
							<Grid item xs={12} sm={6} lg={8}>
								<InputLabel id="voucher-factura-desglose-label" sx={BILLING_FIELD_LABEL_SX}>Desglose del comprobante</InputLabel>
								{importesPreview ? (
									<SoftBox
										role="status"
										aria-labelledby="voucher-factura-desglose-label"
										display="grid"
										gap={1}
										sx={{ gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" } }}
									>
										{[
											{ key: "neto", label: tipoFactura === "C" || opcionIva?.tratamientoIva === "NO_CORRESPONDE" ? "Neto" : "Neto gravado", value: importesPreview.neto },
											{ key: "iva", label: opcionIva?.tratamientoIva === "NO_CORRESPONDE" ? "IVA · No corresponde" : opcionIva?.porcentaje != null ? `IVA ${formatPorcentaje(opcionIva.porcentaje)}` : "IVA", value: importesPreview.iva },
											{ key: "sinIva", label: opcionIva?.tratamientoIva === "NO_CORRESPONDE" ? "Exento / no alcanzado" : TRATAMIENTO_IVA_LABEL[opcionIva?.tratamientoIva] || "Exento / no alcanzado", value: importesPreview.sinIva },
											{ key: "total", label: "Total", value: importesPreview.total, strong: true },
										].map((item) => (
											<SoftBox key={item.key} sx={{ ...BILLING_READONLY_SX, borderStyle: "solid", borderColor: "#e5eaf1", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0, py: 0.6 }}>
												<SoftTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.68rem", lineHeight: 1.2 }}>{item.label}</SoftTypography>
												<SoftTypography variant="button" sx={{ color: "#253451", fontSize: "0.85rem", fontWeight: item.strong ? 700 : 500, lineHeight: 1.3 }}>{money(item.value)}</SoftTypography>
											</SoftBox>
										))}
									</SoftBox>
								) : (
									<SoftBox role="status" aria-labelledby="voucher-factura-desglose-label" sx={{ ...BILLING_READONLY_SX, color: "#8290a8" }}>
										<Icon aria-hidden="true">calculate</Icon>Completá cliente, tratamiento de IVA y monto para ver el desglose
									</SoftBox>
								)}
							</Grid>
						</Grid>
					</BillingSection>

					<BillingSection
						number={4}
						title="Confirmar"
						description="Antes de emitir vas a ver el resumen completo de la factura."
						last
					>
						<SoftBox
							display="flex"
							justifyContent="space-between"
							alignItems={{ xs: "stretch", md: "center" }}
							flexDirection={{ xs: "column", md: "row" }}
							gap={1.5}
						>
							<SoftBox display="flex" alignItems="flex-start" gap={1} aria-live="polite" minWidth={0}>
								<Icon aria-hidden="true" sx={{ mt: 0.15, color: pendientes.length ? "#d97706" : "#059669" }}>
									{pendientes.length ? "pending_actions" : "check_circle_outline"}
								</Icon>
								<SoftTypography variant="button" sx={{ color: "#344563", fontSize: "0.82rem", fontWeight: 500, lineHeight: 1.4 }}>
									{pendientes.length
										? `Falta: ${pendientes.join(" · ")}.`
										: `Listo para emitir Factura ${tipoFactura} por ${money(montoRedondeado)}.`}
								</SoftTypography>
							</SoftBox>
							<SoftButton
								color="primary"
								circular
								disabled={!canPrepareInvoice}
								onClick={openInvoiceConfirmation}
								sx={{ minHeight: 42, whiteSpace: "nowrap" }}
							>
								<Icon>receipt_long</Icon>&nbsp;Facturar
							</SoftButton>
						</SoftBox>
					</BillingSection>
				</Card>
			) : (
				<SoftBox
					role="note"
					display="flex"
					alignItems="flex-start"
					gap={1.25}
					mb={1.5}
					sx={{ p: 1.75, borderRadius: "12px", border: "1px solid #fcd34d", backgroundColor: "#fffbeb" }}
				>
					<Icon aria-hidden="true" sx={{ color: "#b45309" }}>lock_outline</Icon>
					<SoftBox>
						<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#92400e" }}>
							No tenés permiso para facturar este grupo
						</SoftTypography>
						<SoftTypography variant="caption" display="block" sx={{ color: "#92400e" }}>
							Podés consultar el saldo y el historial. Para emitir facturas pedí el permiso &quot;Facturar grupos&quot;.
						</SoftTypography>
					</SoftBox>
				</SoftBox>
			)}

			<SoftBox
				ref={historyRef}
				display="flex"
				justifyContent="space-between"
				alignItems={{ xs: "stretch", md: "flex-end" }}
				flexDirection={{ xs: "column", sm: "row" }}
				gap={1.5}
				mt={3}
				mb={1.5}
			>
				<SoftBox minWidth={0}>
					<SoftTypography variant="h5" fontWeight="bold" display="block" sx={{ color: "#253451", fontSize: { xs: "1.2rem", sm: "1.35rem" }, lineHeight: 1.2 }}>
						Historial de facturación
					</SoftTypography>
					<SoftTypography variant="button" display="block" sx={{ mt: 0.55, color: "#64748b", fontSize: "0.78rem", lineHeight: 1.35 }}>
						Una fila por factura con el resultado de cada período.
					</SoftTypography>
				</SoftBox>
				<SoftBox
					display="flex"
					alignItems="stretch"
					flexDirection={{ xs: "column", md: "row" }}
					gap={1}
					sx={{ width: { xs: "100%", sm: "auto" } }}
				>
					<SoftBox sx={{ width: { xs: "100%", md: 170 } }}>
						<Select
							value={facturasEstado}
							displayEmpty
							onChange={(event) => {
								setFacturasEstado(event.target.value);
								setFacturasPage(1);
								setFacturaResaltadaId(null);
								setFacturaFocoId(null);
							}}
							inputProps={{ "aria-label": "Filtrar facturas por estado" }}
							sx={{ ...BILLING_CONTROL_SX, width: "100%" }}
						>
							{ESTADOS_FACTURA_FILTRO.map((opcion) => (
								<MenuItem key={opcion.value || "todas"} value={opcion.value}>{opcion.label}</MenuItem>
							))}
						</Select>
					</SoftBox>
					<SoftBox sx={{ width: { xs: "100%", md: 320 } }}>
						<SoftInput
							id="voucher-facturas-search"
							value={facturasSearch}
							onChange={(event) => setFacturasSearch(event.target.value)}
							placeholder="Buscar por nro factura ej: 00001234"
							icon={{ component: "search", direction: "left" }}
							inputProps={{ inputMode: "numeric", "aria-label": "Buscar por número de factura" }}
							sx={BILLING_SOFT_INPUT_SX}
						/>
					</SoftBox>
					<Tooltip title={facturasOrder === "DESC" ? "Cambiar a más antiguas primero" : "Cambiar a más recientes primero"}>
						<SoftButton
							variant="outlined"
							color="info"
							size="small"
							onClick={() => {
								setFacturasOrder((currentOrder) => (currentOrder === "DESC" ? "ASC" : "DESC"));
								setFacturasPage(1);
							}}
							aria-label={`Orden actual: ${facturasOrder === "DESC" ? "más recientes primero" : "más antiguas primero"}. Cambiar orden`}
							sx={{
								minHeight: 42,
								minWidth: { xs: "100%", md: 150 },
								px: 1.35,
								borderColor: "#cbd5e1",
								backgroundColor: "#fff",
								color: "#344563",
								boxShadow: "0 4px 12px rgba(37, 52, 81, 0.06)",
								transition: "border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease",
								"&:hover": { borderColor: "#8295b7", backgroundColor: "#f8fafc", boxShadow: "0 6px 16px rgba(37, 52, 81, 0.1)" },
								"&:focus-visible": { outline: "3px solid rgba(52, 99, 206, 0.24)", outlineOffset: 2 },
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							}}
						>
							<Icon aria-hidden="true" sx={{ mr: 0.65, fontSize: "1rem" }}>
								{facturasOrder === "DESC" ? "arrow_downward" : "arrow_upward"}
							</Icon>
							{facturasOrder === "DESC" ? "Más recientes" : "Más antiguas"}
						</SoftButton>
					</Tooltip>
				</SoftBox>
			</SoftBox>
			<Card
				sx={{
					p: { xs: 1, sm: 1.3 },
					boxShadow: "0 5px 18px rgba(37, 52, 81, 0.07)",
					border: "1px solid #dbe3ee",
					borderRadius: "14px",
					backgroundColor: "#fff",
				}}
			>
				{facturaFocoId ? (
					<SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} px={0.5}>
						<SoftTypography variant="caption" sx={{ color: "#475569" }}>
							Mostrando solo la factura #{facturaFocoId}.
						</SoftTypography>
						<SoftButton
							variant="text"
							color="info"
							size="small"
							onClick={() => {
								setFacturaFocoId(null);
								setFacturaResaltadaId(null);
								setFacturasEstado("");
								setFacturasPage(1);
							}}
						>
							Ver todas
						</SoftButton>
					</SoftBox>
				) : null}
				{loadingFacturas ? <Loading /> : facturas.length === 0 ? (
					<SinDatos
						titulo={debouncedFacturasSearch || facturasEstado ? "Sin facturas coincidentes" : "Sin facturas registradas"}
						mensaje={debouncedFacturasSearch || facturasEstado
							? "No se encontraron facturas para el número o el estado elegido."
							: "Las facturas aparecerán aquí."}
					/>
				) : (
					<SoftBox role="table" aria-label="Comparación del historial de facturación">
						<SoftBox
							role="row"
							display={{ xs: "none", xxl: "grid" }}
							sx={{
								gridTemplateColumns: INVOICE_HISTORY_GRID_COLUMNS,
								columnGap: 1.5,
								px: 2,
								py: 1.35,
								borderBottom: "1px solid #cbd5e1",
							}}
						>
							{INVOICE_HISTORY_COLUMNS.map((column, index) => (
								<SoftTypography
									key={column}
									role="columnheader"
									variant="caption"
									fontWeight="bold"
									sx={{ color: "#344563", fontSize: "0.72rem", lineHeight: 1.2, textAlign: index === 0 ? "left" : "center" }}
								>
									{column}
								</SoftTypography>
							))}
						</SoftBox>
						<SoftBox display="grid" gap={1.25} mt={{ xs: 0, xxl: 1.25 }}>
							{facturas.map((factura) => {
								const comparison = factura.comparacion;
								const result = invoiceResult(comparison);
								const badge = badgeFactura(factura);
								const acciones = factura.acciones || {};
								const resaltada = String(factura.id) === String(facturaResaltadaId);
								// Verificar llama a la API de Facturacion: exige el permiso de facturar.
								const puedeVerificar = puedeFacturar && (acciones.verificar ?? factura.estado === "PROCESANDO");
								const incidente = tieneIncidente(factura);
								return (
									<Card
										key={factura.id}
										role="row"
										aria-current={resaltada ? "true" : undefined}
										sx={{
											...(resaltada ? { outline: "2px solid #f59e0b", outlineOffset: 2 } : {}),
											p: { xs: 1.35, sm: 1.6, xxl: 2 },
											display: "grid",
											gridTemplateColumns: {
												xs: "repeat(2, minmax(0, 1fr))",
												md: "repeat(4, minmax(0, 1fr))",
												xxl: INVOICE_HISTORY_GRID_COLUMNS,
											},
											columnGap: { xs: 1.25, xxl: 1.5 },
											rowGap: { xs: 1.5, xxl: 1 },
											alignItems: "center",
											minHeight: { xxl: 118 },
											boxShadow: "0 4px 14px rgba(37, 52, 81, 0.08)",
											border: "1px solid #dbe3ee",
											borderRadius: "12px",
											transition: "border-color 200ms ease, box-shadow 200ms ease",
											"&:hover": { borderColor: "#c2cfdf", boxShadow: "0 7px 20px rgba(37, 52, 81, 0.11)" },
											"@media (prefers-reduced-motion: reduce)": { transition: "none" },
										}}
									>
										<SoftBox role="cell" minWidth={0} sx={{ gridColumn: { xs: "1 / -1", xxl: "auto" }, pr: { xxl: 1.5 }, borderRight: { xxl: "1px solid #e2e8f0" } }}>
											<SoftBadge variant="gradient" color={badge.color} badgeContent={badge.label} />
											<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ mt: 0.65, color: "#253451", fontSize: "0.84rem", lineHeight: 1.3 }}>
												Factura {factura.tipoFactura} {factura.numeroDeFacturaAfip
													? `N° ${factura.numeroDeFacturaAfip}`
													: `· #${factura.id}`}
											</SoftTypography>
											<SoftTypography variant="caption" color="secondary" display="block" sx={{ mt: 0.45 }}>
												{factura.sede?.nombre || "-"}{" · "}{clientName(factura.clienteSnapshot)}
												{factura.actividadCodigo
													? ` · ${factura.actividadCodigo} ${ivaLabel(factura.tratamientoIva, factura.alicuotaIvaId)}`
													: ""}
											</SoftTypography>
											{/* {Number(factura.montoExceso || 0) > 0 ? (
												<SoftTypography variant="caption" display="block" sx={{ mt: 0.2, color: "#b45309", fontWeight: 600 }}>
													Exceso sobre el sugerido: {money(factura.montoExceso)}
												</SoftTypography>
											) : null} */}
										</SoftBox>
										<InvoiceHistoryCell label="Fecha emisión" sx={{ textAlign: { xxl: "center" } }}>
											<SoftTypography variant="button" sx={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 500 }}>
												{fechaEmisionFactura(factura, formatInvoiceEmissionDate)}
											</SoftTypography>
										</InvoiceHistoryCell>
										<InvoiceHistoryCell label="Saldo arrastrado" sx={{ textAlign: { xxl: "center" } }}>
											<SoftTypography variant="button" fontWeight="bold">{comparison ? signedMoney(comparison.saldoArrastrado) : "-"}</SoftTypography>
										</InvoiceHistoryCell>
										<InvoiceHistoryCell label="Nuevos consumos" sx={{ textAlign: { xxl: "center" } }}>
											<SoftTypography variant="button" fontWeight="bold">{comparison ? money(comparison.nuevosDescuentos) : "-"}</SoftTypography>
										</InvoiceHistoryCell>
										<InvoiceHistoryCell label="Sugerido" sx={{ textAlign: { xxl: "center" } }}>
											<SoftTypography variant="button" fontWeight="bold">{comparison ? money(comparison.sugerido) : "-"}</SoftTypography>
										</InvoiceHistoryCell>
										<InvoiceHistoryCell label="Facturado" sx={{ textAlign: { xxl: "center" } }}>
											<SoftTypography variant="button" fontWeight="bold">{money(comparison?.facturado ?? factura.total)}</SoftTypography>
										</InvoiceHistoryCell>
										<InvoiceHistoryCell label="Resultado">
											<SoftBox display="flex" alignItems="center" justifyContent={{ xs: "flex-start", xxl: "center" }} gap={0.7}>
												<SoftBox aria-hidden="true" sx={{ width: 9, height: 9, minWidth: 9, borderRadius: "50%", backgroundColor: result.color }} />
												<SoftTypography variant="button" fontWeight="bold" sx={{ color: result.color }}>{result.label}</SoftTypography>
											</SoftBox>
										</InvoiceHistoryCell>
										<InvoiceHistoryCell label="Acciones" sx={{ textAlign: { xxl: "center" } }}>
											<SoftBox display="flex" alignItems="center" justifyContent={{ xs: "flex-start", xxl: "center" }} gap={0.65}>
												{/* {factura.estado === "EMITIDA" ? (
													<Tooltip title="Descargar Excel">
														<span>
															<SoftButton
																color="uproGreen"
																size="small"
																circular
																iconOnly
																disabled={downloadingFacturaId !== null}
																onClick={() => handleHistoricalExcelDownload(factura)}
																aria-label={`Descargar Excel de la factura ${factura.numeroDeFacturaAfip || factura.id}`}
															>
																<Icon>{Number(downloadingFacturaId) === Number(factura.id) ? "hourglass_top" : "table_view"}</Icon>
															</SoftButton>
														</span>
													</Tooltip>
												) : null} */}
												{puedeVerificar ? (
													<Tooltip title="Verificar en ARCA">
														<span>
															<SoftButton
																color="info"
																size="small"
																circular
																iconOnly
																disabled={verificandoFacturaId !== null}
																onClick={() => handleVerificarFactura(factura)}
																aria-label={`Verificar la factura ${factura.id} en ARCA`}
															>
																<Icon>{Number(verificandoFacturaId) === Number(factura.id) ? "hourglass_top" : "sync"}</Icon>
															</SoftButton>
														</span>
													</Tooltip>
												) : null}
												{puedeFacturar && acciones.reintentar ? (
													<Tooltip title="Reintentar con una nueva decisión de emisión">
														<span>
															<SoftButton
																color="warning"
																size="small"
																circular
																iconOnly
																disabled={reintentando}
																onClick={() => abrirReintento(factura)}
																aria-label={`Reintentar la factura ${factura.id}`}
															>
																<Icon>replay</Icon>
															</SoftButton>
														</span>
													</Tooltip>
												) : null}
												{puedeFacturar && acciones.descartar ? (
													<Tooltip title="Descartar la factura">
														<span>
															<SoftButton
																color="secondary"
																size="small"
																circular
																iconOnly
																disabled={descartando}
																onClick={() => {
																	setDescarteFactura(factura);
																	setMotivoDescarte("");
																}}
																aria-label={`Descartar la factura ${factura.id}`}
															>
																<Icon>block</Icon>
															</SoftButton>
														</span>
													</Tooltip>
												) : null}
												{puedeFacturar && incidente ? (
													<Tooltip title="Marcar incidente revisado">
														<span>
															<SoftButton
																color="error"
																size="small"
																circular
																iconOnly
																disabled={resolviendoIncidente}
																onClick={() => {
																	setIncidenteFactura(factura);
																	setMotivoIncidente("");
																}}
																aria-label={`Marcar incidente revisado de la factura ${factura.id}`}
															>
																<Icon>fact_check</Icon>
															</SoftButton>
														</span>
													</Tooltip>
												) : null}
												{factura.estado === "EMITIDA" && factura.CAE ? (
													<Tooltip title="Ver comprobante">
														<SoftButton color="uproYellow" size="small" circular iconOnly onClick={() => setSelectedFactura(factura)} aria-label={`Ver factura ${factura.numeroDeFacturaAfip || factura.id}`}>
															<Icon>visibility</Icon>
														</SoftButton>
													</Tooltip>
												) : null}
											</SoftBox>
										</InvoiceHistoryCell>
										{factura.estado === "DESCARTADA" ? (
											<SoftBox
												role="cell"
												display="flex"
												alignItems="flex-start"
												gap={0.6}
												sx={{ gridColumn: "1 / -1", p: 0.8, borderRadius: "8px", backgroundColor: "#f1f5f9", color: "#475569" }}
											>
												<Icon aria-hidden="true" sx={{ mt: "1px", fontSize: "1rem" }}>block</Icon>
												<SoftTypography variant="caption" sx={{ color: "inherit", lineHeight: 1.35 }}>
													Descartada
													{factura.fechaDescarte ? ` el ${dayjs(factura.fechaDescarte).format("DD/MM/YYYY HH:mm")}` : ""}
													{invoiceUserName(factura.usuarioDescarte) ? ` por ${invoiceUserName(factura.usuarioDescarte)}` : ""}
													{factura.motivoDescarte ? `: ${factura.motivoDescarte}` : ""}
													. No suma a lo facturado ni anula comprobantes fiscales.
												</SoftTypography>
											</SoftBox>
										) : null}
										{factura.error || comparison?.advertencia ? (
											<SoftBox
												role="cell"
												display="flex"
												alignItems="flex-start"
												gap={0.6}
												sx={{ gridColumn: "1 / -1", p: 0.8, borderRadius: "8px", backgroundColor: "#fffbeb", color: "#92400e" }}
											>
												<Icon aria-hidden="true" sx={{ mt: "1px", fontSize: "1rem" }}>warning_amber</Icon>
												<SoftTypography variant="caption" sx={{ color: "inherit", lineHeight: 1.35 }}>
													{factura.error || comparison.advertencia}
												</SoftTypography>
											</SoftBox>
										) : null}
										{factura.estado === "ERROR" && !acciones.reintentar && acciones.motivo ? (
											<SoftTypography role="cell" variant="caption" sx={{ gridColumn: "1 / -1", color: "#7f1d1d" }}>
												{acciones.motivo}
											</SoftTypography>
										) : null}
									</Card>
								);
							})}
						</SoftBox>
						{facturasPagination.pages > 1 ? (
							<SoftBox mt={1.25}>
								<CustomPagination
									loading={loadingFacturas}
									length={facturas.length}
									total={facturasPagination.total}
									pages={facturasPagination.pages}
									page={facturasPage}
									setPage={setFacturasPage}
									noCard
								/>
							</SoftBox>
						) : null}
					</SoftBox>
				)}
			</Card>

			<Dialog
				open={Boolean(falloEmision)}
				disableEscapeKeyDown
				fullWidth
				maxWidth="sm"
				aria-labelledby="error-emision-voucher-title"
				aria-describedby="error-emision-voucher-description"
				PaperProps={{ sx: { borderRadius: "16px" } }}
			>
				<DialogTitle id="error-emision-voucher-title">
					<SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
						<SoftTypography variant="h5" fontWeight="bold">
							{falloEmision?.diagnostico?.titulo || (falloEmision?.estado === "PROCESANDO" ? "Factura pendiente de confirmación" : "No se pudo emitir la factura")}
						</SoftTypography>
						<SoftButton
							iconOnly circular color="secondary" size="small"
							aria-label={falloEmision?.solicitud ? "Descartar intento de facturación" : "Cerrar detalle de facturación"}
							disabled={facturando}
							onClick={falloEmision?.solicitud ? descartarIntento : () => setFalloEmision(null)}
						>
							<Icon>close</Icon>
						</SoftButton>
					</SoftBox>
				</DialogTitle>
				<DialogContent dividers id="error-emision-voucher-description">
					<SoftBox role="alert" sx={{ p: 1.5, borderRadius: "10px", backgroundColor: falloEmision?.estado === "PROCESANDO" ? "#fffbeb" : "#fef2f2" }}>
						{falloEmision?.mensaje?.split(" | ").map((linea, index) => (
							<SoftTypography key={`${index}-${linea}`} variant="button" display="block" sx={{ color: "#7f1d1d", wordBreak: "break-word" }}>
								{linea}
							</SoftTypography>
						))}
					</SoftBox>
					{falloEmision?.diagnostico?.dato ? <SoftTypography variant="button" display="block" sx={{ mt: 1.5, wordBreak: "break-word" }}>Dato a revisar: {falloEmision.diagnostico.dato}.</SoftTypography> : null}
					{falloEmision?.diagnostico?.contexto ? <SoftTypography variant="button" display="block" sx={{ mt: 1.5, wordBreak: "break-word" }}>{falloEmision.diagnostico.contexto}</SoftTypography> : null}
					{falloEmision?.diagnostico?.sugerencias?.map((texto, index) => (
						<SoftBox key={`sugerencia-${index}`} sx={{ mt: 1.5, p: 1.5, borderRadius: "10px", backgroundColor: "#eff6ff" }}>
							<SoftTypography variant="button" fontWeight="bold" display="block">Sugerencia</SoftTypography>
							<SoftTypography variant="button" display="block" sx={{ wordBreak: "break-word" }}>{texto}</SoftTypography>
						</SoftBox>
					))}
					{falloEmision?.diagnostico?.advertencias?.map((texto, index) => <SoftTypography key={`advertencia-${index}`} variant="button" display="block" sx={{ mt: 1.5, color: "#475569", wordBreak: "break-word" }}>{texto}</SoftTypography>)}
					{falloEmision?.diagnostico?.origen !== "ARCA" ? <SoftTypography variant="button" display="block" sx={{ mt: 1.5, color: "#475569" }}>
						{falloEmision?.diagnostico?.estadoEmision || (falloEmision?.solicitud
							? "La factura no se envió. Volvé al formulario y corregí la causa indicada. Descartar restablece los campos del intento."
							: falloEmision?.intentarMasTarde
								? "La factura no llegó a emitirse. Podés cerrar este aviso y volver a intentar más tarde con los datos que cargaste."
								: falloEmision?.idFactura
									? `La factura #${falloEmision.idFactura} está registrada. Revisá su estado y las acciones disponibles en el historial.`
									: "Revisá el historial antes de volver a facturar para comprobar el resultado del envío.")}
					</SoftTypography> : null}
					{falloEmision?.diagnostico?.accion ? <SoftTypography variant="button" display="block" sx={{ mt: 1.5, wordBreak: "break-word" }}>{falloEmision.diagnostico.accion}</SoftTypography> : null}
					{falloEmision?.diagnostico?.codigo || falloEmision?.diagnostico?.referencia ? <SoftTypography variant="caption" display="block" sx={{ mt: 2, color: "#64748b", wordBreak: "break-all" }}>Referencia para soporte: {[falloEmision.diagnostico.codigo, falloEmision.diagnostico.referencia].filter(Boolean).join(" · ")}</SoftTypography> : null}
				</DialogContent>
				<DialogActions sx={{
					p: 2, gap: 1, flexWrap: "wrap",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "stretch", sm: "center" },
					"& > button": { width: { xs: "100%", sm: "auto" } },
					"& > :not(:first-of-type)": { ml: 0 },
				}}>
					{falloEmision?.solicitud ? (
						<>
							<SoftButton color="secondary" disabled={facturando} onClick={descartarIntento}>Descartar</SoftButton>
							<SoftButton color="secondary" variant="outlined" disabled={facturando} onClick={modificarIntento}>Modificar datos</SoftButton>
						</>
					) : falloEmision?.intentarMasTarde ? (
						<SoftButton color="uproPink" onClick={() => setFalloEmision(null)}>Volver a intentar más tarde</SoftButton>
					) : (
						<SoftButton color="uproPink" onClick={() => {
							const id = falloEmision?.idFactura;
							setFalloEmision(null);
							if (id) irAFactura(id);
							else historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
						}}>Ver historial</SoftButton>
					)}
				</DialogActions>
			</Dialog>

			<VoucherClienteDialog
				open={puedeAdministrarClientes && clientDialogOpen}
				onClose={() => setClientDialogOpen(false)}
				onSaved={handleClientSaved}
			/>
			<Dialog
				open={confirmOpen}
				onClose={facturando ? undefined : () => setConfirmOpen(false)}
				fullWidth
				maxWidth="md"
				aria-labelledby="confirmar-factura-voucher-title"
				PaperProps={{
					sx: {
						m: { xs: 1.5, sm: 3 },
						width: "100%",
						borderRadius: { xs: "16px", sm: "20px" },
						boxShadow: "0 24px 70px rgba(37, 52, 81, 0.18)",
						overflow: "hidden",
					},
				}}
			>
				<DialogTitle id="confirmar-factura-voucher-title" sx={{ p: { xs: 2, sm: 3 } }}>
					<SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
						<SoftBox display="flex" alignItems="center" gap={{ xs: 1.25, sm: 2 }} minWidth={0}>
							<SoftBox
								display="grid"
								sx={{
									color: "#ec087a",
								}}
							>
								<Icon aria-hidden="true">fact_check</Icon>
							</SoftBox>
							<SoftTypography
								variant="h4"
								fontWeight="bold"
								sx={{ color: "#253451", fontSize: { xs: "1.12rem", sm: "1.55rem" }, lineHeight: 1.2 }}
							>
								Confirmar factura
							</SoftTypography>
						</SoftBox>
						<SoftButton
							color="secondary"
							circular
							size="small"
							iconOnly
							onClick={() => setConfirmOpen(false)}
							disabled={facturando}
							aria-label="Cerrar confirmación"
						>
							<Icon>close</Icon>
						</SoftButton>
					</SoftBox>
				</DialogTitle>
				<SoftBox mx={{ xs: 2, sm: 3 }} sx={{ borderTop: "1px solid #e5eaf1" }} />
				<DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
					<SoftBox
						sx={{
							px: { xs: 1.25, sm: 2.5 },
							py: 0.5,
							border: "1px solid #dbe3ee",
							borderRadius: "12px",
							backgroundColor: "#fff",
						}}
					>
						<InvoiceConfirmationRow icon="location_on" label="Sede" value={selectedSede?.nombre || "-"} />
						<InvoiceConfirmationRow icon="person_outline" label="Cliente" value={`${clientName(cliente)} · CUIT ${cliente?.cuit || "-"}`} />
						<InvoiceConfirmationRow icon="calendar_today" label="Fecha" value={dayjs().format("DD/MM/YYYY")} />
						<InvoiceConfirmationRow icon="account_balance_wallet" label={saldoLabel} value={saldoDisplay} highlight />
						<InvoiceConfirmationRow icon="lightbulb_outline" label="Monto sugerido" value={money(montoSugerido)} />
						<InvoiceConfirmationRow icon="monetization_on" label="Importe a facturar" value={money(montoRedondeado)} />
						<InvoiceConfirmationRow icon="sell" label="Tipo" value={`Factura ${tipoFactura}`} />
						<InvoiceConfirmationRow
							icon="work_outline"
							label="Actividad"
							value={`${actividad?.codigo || "-"} · ${opcionIva?.descripcion || "-"}`}
						/>
						<InvoiceConfirmationRow
							icon="description"
							label="Concepto"
							value={`Consumo de vouchers — Grupo ${grupo?.nombre || "-"} — al ${dayjs().format("DD/MM/YYYY")}`}
							last
						/>
					</SoftBox>
					{montoSuperaSugerido ? (
						<SoftBox
							role="alert"
							sx={{
								mt: 1.5,
								p: { xs: 1.5, sm: 2 },
								border: "1px solid #f4c37d",
								borderRadius: "12px",
								backgroundColor: "#fff8eb",
							}}
						>
							<SoftBox display="flex" alignItems="flex-start" gap={1}>
								<Icon aria-hidden="true" sx={{ color: "#b45309", mt: 0.1 }}>warning_amber</Icon>
								<SoftBox>
									<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#92400e" }}>
										El importe supera el monto sugerido en {money(montoExceso)}
									</SoftTypography>
									<SoftTypography variant="caption" display="block" sx={{ mt: 0.4, color: "#78350f" }}>
										La operación dejará un saldo a favor de {money(Math.abs(Math.min(saldoResultante, 0)))}.
									</SoftTypography>
								</SoftBox>
							</SoftBox>
						</SoftBox>
					) : null}
				</DialogContent>
				<DialogActions
					sx={{
						px: { xs: 2, sm: 3 },
						py: { xs: 2, sm: 2.5 },
						borderTop: "1px solid #e5eaf1",
						gap: 1.25,
						flexDirection: { xs: "column-reverse", sm: "row" },
					}}
				>
					<SoftButton
						color="secondary"
						onClick={() => setConfirmOpen(false)}
						disabled={facturando}
						sx={{
							width: { xs: "100%", sm: "auto" },
							minHeight: 48,
							px: 2.75,
							border: "1px solid #dbe3ee",
							borderRadius: "10px",
							boxShadow: "0 5px 14px rgba(37, 52, 81, 0.1)",
							color: "#253451",
						}}
					>
						<Icon>close</Icon>&nbsp;Cancelar
					</SoftButton>
					<SoftButton
						color="uproPink"
						onClick={() => handleInvoice(false)}
						disabled={!canInvoice}
						sx={{
							width: { xs: "100%", sm: "auto" },
							minHeight: 48,
							px: 3,
							borderRadius: "10px",
						}}
					>
						<Icon>description</Icon>&nbsp;{facturando ? "Procesando ARCA..." : "Emitir factura"}
					</SoftButton>
					<SoftButton
						color="uproGreen"
						onClick={() => handleInvoice(true)}
						disabled={!canInvoice}
						sx={{
							width: { xs: "100%", sm: "auto" },
							minHeight: 48,
							px: 3,
							borderRadius: "10px",
							boxShadow: "0 8px 18px rgba(236, 8, 122, 0.22)",
						}}
					>
						<Icon>download</Icon>&nbsp;{facturando ? "Procesando ARCA..." : "Emitir factura y descargar Excel"}
					</SoftButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(reintentoFactura)}
				onClose={reintentando ? undefined : () => setReintentoFactura(null)}
				fullWidth
				maxWidth="sm"
				aria-labelledby="reintentar-factura-voucher-title"
			>
				<DialogTitle id="reintentar-factura-voucher-title">
					Reintentar factura #{reintentoFactura?.id}
				</DialogTitle>
				<DialogContent dividers>
					<SoftTypography variant="caption" display="block" sx={{ color: "#475569", mb: 1.5 }}>
						Se envía una nueva decisión de emisión sobre la misma factura por {money(reintentoFactura?.total)}.
						Solo se pueden corregir la letra, la actividad y el IVA. Los datos del receptor se toman de la ficha
						actual del cliente: si el documento es incorrecto, corregilo en la ficha y volvé a reintentar.
						Para cambiar el monto o el cliente, descartá esta factura y emití una nueva.
					</SoftTypography>
					{reintentoFactura?.error ? (
						<SoftBox sx={{ mb: 1.5, p: 1, borderRadius: "8px", backgroundColor: "#fef2f2" }}>
							{reintentoFactura.error.split(" | ").map((linea) => (
								<SoftTypography key={linea} variant="caption" display="block" sx={{ color: "#7f1d1d", wordBreak: "break-word" }}>
									{linea}
								</SoftTypography>
							))}
						</SoftBox>
					) : null}
					<Grid container spacing={1.5}>
						<Grid item xs={12} sm={4}>
							<InputLabel id="reintento-tipo-factura-label" htmlFor="reintento-tipo-factura" sx={BILLING_FIELD_LABEL_SX}>Tipo de factura</InputLabel>
							<Select
								id="reintento-tipo-factura"
								labelId="reintento-tipo-factura-label"
								value={reintento.tipoFactura}
								displayEmpty
								onChange={(event) => setReintento((actual) => ({ ...actual, tipoFactura: event.target.value }))}
								sx={{ ...BILLING_CONTROL_SX, width: "100%" }}
							>
								{TIPOS_FACTURA.map((tipo) => <MenuItem key={tipo} value={tipo}>Factura {tipo}</MenuItem>)}
							</Select>
						</Grid>
						<Grid item xs={12} sm={8}>
							<InputLabel id="reintento-actividad-label" htmlFor="reintento-actividad" sx={BILLING_FIELD_LABEL_SX}>Actividad</InputLabel>
							<Select
								id="reintento-actividad"
								labelId="reintento-actividad-label"
								value={reintento.actividadCodigo}
								displayEmpty
								onChange={(event) => {
									const codigo = event.target.value;
									setReintento((actual) => ({
										...actual,
										actividadCodigo: codigo,
										opcionIvaClave: actividades.find((item) => item.codigo === codigo)?.opcionIvaSugerida || actual.opcionIvaClave,
									}));
								}}
								sx={{ ...BILLING_CONTROL_SX, width: "100%" }}
							>
								{actividades.map((item) => (
									<MenuItem key={item.codigo} value={item.codigo}>{item.codigo} · {item.descripcion}</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12}>
							<InputLabel id="reintento-iva-label" htmlFor="reintento-iva" sx={BILLING_FIELD_LABEL_SX}>IVA</InputLabel>
							<Select
								id="reintento-iva"
								labelId="reintento-iva-label"
								value={reintento.opcionIvaClave}
								displayEmpty
								onChange={(event) => setReintento((actual) => ({ ...actual, opcionIvaClave: event.target.value }))}
								sx={{ ...BILLING_CONTROL_SX, width: "100%" }}
							>
								{opcionesIva.map((opcion) => <MenuItem key={opcion.clave} value={opcion.clave}>{opcion.descripcion}</MenuItem>)}
							</Select>
						</Grid>
					</Grid>
					{reintentoPendiente ? (
						<SoftTypography variant="caption" display="block" sx={{ ...BILLING_HELPER_SX, color: "#b45309" }}>
							{reintentoPendiente}
						</SoftTypography>
					) : null}
				</DialogContent>
				<DialogActions>
					<SoftButton color="secondary" onClick={() => setReintentoFactura(null)} disabled={reintentando}>
						Cancelar
					</SoftButton>
					<SoftButton color="uproPink" onClick={handleReintentar} disabled={Boolean(reintentoPendiente) || reintentando}>
						<Icon>replay</Icon>&nbsp;{reintentando ? "Procesando ARCA..." : "Reintentar emisión"}
					</SoftButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(descarteFactura)}
				onClose={descartando ? undefined : () => setDescarteFactura(null)}
				fullWidth
				maxWidth="sm"
				aria-labelledby="descartar-factura-voucher-title"
			>
				<DialogTitle id="descartar-factura-voucher-title">
					Descartar factura #{descarteFactura?.id}
				</DialogTitle>
				<DialogContent dividers>
					<SoftTypography variant="caption" display="block" sx={{ color: "#475569", mb: 1.5 }}>
						La factura por {money(descarteFactura?.total)} quedará DESCARTADA: no suma a lo facturado, no se
						puede reintentar y conserva su historial. Descartar no anula ningún comprobante fiscal.
					</SoftTypography>
					<InputLabel htmlFor="motivo-descarte" sx={BILLING_FIELD_LABEL_SX}>Motivo (obligatorio)</InputLabel>
					<SoftInput
						id="motivo-descarte"
						multiline
						rows={3}
						value={motivoDescarte}
						onChange={(event) => setMotivoDescarte(event.target.value)}
						inputProps={{
							maxLength: MOTIVO_DESCARTE_MAX,
							"aria-invalid": Boolean(motivoDescarte && motivoDescarteError),
							"aria-describedby": motivoDescarte && motivoDescarteError ? "motivo-descarte-error" : undefined,
						}}
						placeholder="Ej.: se emitirá una nueva factura con otro cliente"
					/>
					{motivoDescarte && motivoDescarteError ? (
						<SoftTypography id="motivo-descarte-error" role="alert" variant="caption" display="block" sx={{ ...BILLING_HELPER_SX, color: "#b91c1c" }}>
							{motivoDescarteError}
						</SoftTypography>
					) : null}
				</DialogContent>
				<DialogActions>
					<SoftButton color="secondary" onClick={() => setDescarteFactura(null)} disabled={descartando}>
						Cancelar
					</SoftButton>
					<SoftButton color="error" onClick={handleDescartar} disabled={Boolean(motivoDescarteError) || descartando}>
						<Icon>block</Icon>&nbsp;{descartando ? "Descartando..." : "Descartar factura"}
					</SoftButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(incidenteFactura)}
				onClose={resolviendoIncidente ? undefined : () => setIncidenteFactura(null)}
				fullWidth
				maxWidth="sm"
				aria-labelledby="incidente-factura-voucher-title"
			>
				<DialogTitle id="incidente-factura-voucher-title">
					Marcar incidente revisado · factura #{incidenteFactura?.id}
				</DialogTitle>
				<DialogContent dividers>
					<SoftTypography variant="caption" display="block" sx={{ color: "#475569", mb: 1.5 }}>
						Confirmá que el incidente fiscal fue revisado (por ejemplo, con soporte de la API de Facturación o con
						contaduría). Solo se registra quién lo revisó y por qué: no cambia el estado de la factura, no anula ni
						aplica comprobantes y no modifica los datos fiscales.
					</SoftTypography>
					{textosRevisionIncidente(incidenteFactura).map((texto) => (
						<SoftTypography key={texto} variant="caption" display="block" sx={{ color: "#92400e", mb: 1 }}>
							{texto}
						</SoftTypography>
					))}
					{(incidenteFactura?.incidentes || [])
						.map((incidente) => (
							<SoftTypography key={incidente.id} variant="caption" display="block" sx={{ color: "#7f1d1d", mb: 0.5 }}>
								Incidente {incidente.id}: {intervencionLabel(incidente.intervencion)}
							</SoftTypography>
						))}
					<InputLabel htmlFor="motivo-incidente" sx={{ ...BILLING_FIELD_LABEL_SX, mt: 1 }}>Qué se revisó (obligatorio)</InputLabel>
					<SoftInput
						id="motivo-incidente"
						multiline
						rows={3}
						value={motivoIncidente}
						onChange={(event) => setMotivoIncidente(event.target.value)}
						inputProps={{
							maxLength: MOTIVO_DESCARTE_MAX,
							"aria-invalid": Boolean(motivoIncidente && motivoIncidenteError),
							"aria-describedby": motivoIncidente && motivoIncidenteError ? "motivo-incidente-error" : undefined,
						}}
						placeholder="Ej.: se verificó con soporte que el número pertenece a otro comprobante"
					/>
					{motivoIncidente && motivoIncidenteError ? (
						<SoftTypography id="motivo-incidente-error" role="alert" variant="caption" display="block" sx={{ ...BILLING_HELPER_SX, color: "#b91c1c" }}>
							{motivoIncidenteError}
						</SoftTypography>
					) : null}
				</DialogContent>
				<DialogActions>
					<SoftButton color="secondary" onClick={() => setIncidenteFactura(null)} disabled={resolviendoIncidente}>
						Cancelar
					</SoftButton>
					<SoftButton color="error" onClick={handleResolverIncidente} disabled={Boolean(motivoIncidenteError) || resolviendoIncidente}>
						<Icon>fact_check</Icon>&nbsp;{resolviendoIncidente ? "Guardando..." : "Marcar revisado"}
					</SoftButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(selectedFactura)}
				onClose={() => setSelectedFactura(null)}
				fullWidth
				maxWidth="lg"
				aria-labelledby="comprobante-voucher-title"
			>
				<DialogTitle id="comprobante-voucher-title">
					Factura {selectedFactura?.tipoFactura || ""} · N° {selectedFactura?.numeroDeFacturaAfip || ""}
				</DialogTitle>
				<DialogContent dividers sx={{ overflowX: "auto", p: { xs: 1, sm: 2 } }}>
					{selectedFactura ? <VoucherFacturaImpresion factura={selectedFactura} descripcion={facturaDescription} /> : null}
				</DialogContent>
				<DialogActions>
					<SoftButton color="secondary" circular onClick={() => setSelectedFactura(null)}>Cerrar</SoftButton>
				</DialogActions>
			</Dialog>
		</Card>
	);
}

SummaryIcon.propTypes = {
	icon: PropTypes.string.isRequired,
	color: PropTypes.string.isRequired,
};

UsageSummaryCard.propTypes = {
	items: PropTypes.arrayOf(PropTypes.shape({
		key: PropTypes.string.isRequired,
		icon: PropTypes.string.isRequired,
		eyebrow: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		value: PropTypes.string.isRequired,
		percentage: PropTypes.string.isRequired,
		color: PropTypes.string.isRequired,
	})).isRequired,
};

FinancialSummaryCard.propTypes = {
	rows: PropTypes.arrayOf(PropTypes.shape({
		key: PropTypes.string.isRequired,
		icon: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		value: PropTypes.string.isRequired,
		detail: PropTypes.string.isRequired,
		color: PropTypes.string.isRequired,
	})).isRequired,
};

LastInvoiceCard.propTypes = {
	date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	user: PropTypes.string,
};

InvoiceConfirmationRow.propTypes = {
	icon: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.node.isRequired,
	highlight: PropTypes.bool,
	last: PropTypes.bool,
};

InvoiceHistoryCell.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	sx: PropTypes.object,
};

BillingSection.propTypes = {
	number: PropTypes.number.isRequired,
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	last: PropTypes.bool,
};

VoucherFacturacionTab.propTypes = {
	puedeFacturar: PropTypes.bool.isRequired,
	puedeAdministrarClientes: PropTypes.bool.isRequired,
	grupo: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		nombre: PropTypes.string,
		idSede: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		sede: PropTypes.object,
		clienteFacturacion: PropTypes.object,
	}).isRequired,
};
